/**
 * Data Explorer - Combination View Module
 * 
 * Advanced multi-dimensional data exploration with cascading filters and portfolio analysis
 * Supports complex filtering across licensors, properties, products, channels, territories, and demographics
 * 
 * Features:
 * - Multi-step filtering with cascading dropdowns
 * - Portfolio view with data aggregation
 * - Combination-based filtering (prevents invalid filter combinations)
 * - Saved views/filters with persistent storage
 * - Slide-out filter and columns management panels
 * - Large dataset handling with performance optimization
 * - URL parameter support for deep linking (type, filtervalue, viewid, viewname)
 * - CSV export functionality
 * 
 * View Modes:
 * - Combination view: Multi-level filtering with cascading relationships
 * - Dimension-based filtering across 5+ dimensions
 * 
 * State Architecture:
 * Main filters:
 *   - combinationFilters: Active applied filters
 *   - tempFilters: Temporary filters (preview before apply)
 * 
 * Dimension selectors:
 *   - Licensor & Property (linked hierarchy)
 *   - Product: 3-level hierarchy (L1/L2/L3)
 *   - Channel: 3-level hierarchy (L1/L2/L3)
 *   - Territory: 2-level hierarchy (L1/L2)
 *   - Demographics: Gender and Age Group
 * 
 * API Integration:
 *   - getDataExplorerDetails(filters): Get filtered data and combinations
 *   - getLicensorDataExploerView(data): Get portfolio view
 *   - getDataExplorerPortfolio(data): Get portfolio analysis
 *   - apiCall: Save/load/delete filters from /agent/views/ endpoints
 *   - fetchTaxonomyData: Get taxonomy options
 * 
 * Data Flow:
 * 1. URL params (type, filtervalue, viewid) initialize filters
 * 2. Filter selections update tempFilters (preview state)
 * 3. Apply button → tempFilters → combinationFilters → API call
 * 4. API returns data, combinations, and dimension options
 * 5. Combinations object enables cascading filter logic
 * 6. Large table renders with pagination (50 rows/page)
 * 
 * Performance Considerations:
 * - useMemo for cascading filter calculations (expensive)
 * - loadingRef to manage state during large data loads
 * - Pagination for tables with 1000+ rows
 * - Combination data prevents invalid filter combinations
 * 
 * @module DataExplorerCombination
 * @requires react
 * @requires react-router-dom
 * @requires lucide-react
 * @requires react-select
 * @requires jquery
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { RefreshCcw,ListChecks ,SearchCheck,Grid3X3,Database, Layers, ShoppingBag, ShoppingCart, Globe, Search, ChevronLeft, ChevronRight, AlertTriangle, Fuel } from 'lucide-react';
import { API_SUCCESS_RESPONSE_CODE,API_SUCCESS_RESPONSE_CREATED_CODE } from "../../utils/config_mapping";
import PageLoader from '../common/pageloader';
import {getLicensorDataExploerView,getDataExplorerDetails,getDataExplorerPortfolio} from "../../services/admin"
import $ from "jquery";
import { hasPageAccess,accessDenied } from "../../utils/page_access";
import { apiCall } from "../../utils/server_utils";
import { API_SERVER_URL } from "../../utils/config_ip_details";
import Multiselect from 'multiselect-react-dropdown';
import { CommonMultiSelect } from '../common/MultiSelect';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, useParams, Link } from "react-router-dom";
import { default as ReactSelect, components } from "react-select";
import CommonIcon from '../common/customicon';
import {fetchTaxonomyData} from "../../services/admin"

/* ========== CUSTOM REACT-SELECT OPTION COMPONENT ========== */
/**
 * Custom Option component for React-Select with checkbox
 * Renders each dropdown option with a checkbox for multi-select
 * 
 * @component
 * @param {Object} props - React-Select Option props
 * @param {string} props.label - Display label for option
 * @param {boolean} props.isSelected - Whether option is selected
 * @returns {JSX.Element} Option with checkbox
 */
const Option = (props) => {
  return (
    <div>
      <components.Option {...props}>
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => null}
          style={{ marginRight: '8px' }}
        />
        <label>{props.label}</label>
      </components.Option>
    </div>
  );
};

/* ========== MAIN COMBINATION VIEW COMPONENT ========== */
/**
 * Combination view component for multi-dimensional data exploration
 * 
 * @component
 * @returns {JSX.Element} Complete data explorer with filters, table, and panels
 */
export const Combination = () => {
  // ========== ROUTER HOOKS ==========
  const navigate = useNavigate();
  const location = useLocation();
  const { type, filtervalue, viewid, viewname } = useParams();
  const { item } = location.state || {};

  // ========== PAGE METADATA ==========
  let pageName = "Data Explorer";

  /* ========== STATE MANAGEMENT ==========
   * 
   * State is organized into logical groups:
   * 1. Tab/Portfolio data
   * 2. Filter selections
   * 3. API data
   * 4. UI state (panels, modals)
   * 5. Performance tracking (loading, refs)
   */

  // ========== TAB DATA STATE ==========
  /**
   * Tab 1 data structure
   * Holds portfolio and filter options
   */
  const [tab1, setTab1] = useState({
    portfolioData: [],      // Full portfolio data
    licensors: [],          // Licensor options
    properties: [],         // Property options
    products: [],           // Product options
    channels: [],           // Channel options
    territories: [],        // Territories (deprecated)
    territory: [],          // Territory (current)
    selectedLicensors: [],  // Selected licensors
    selectedProperties: [], // Selected properties
    selectedProducts: [],   // Selected products
    selectedChannels: [],   // Selected channels
    selectedTerritories: [] // Selected territories
  });

  // ========== FILTER SELECTION STATE ==========
  /**
   * Individual selection states for tab 1
   * Used to track user selections before applying
   */
  const [tab1selectedLicensors, settab1SelectedLicensors] = useState([]);
  const [tab1selectedProperties, settab1SelectedProperties] = useState([]);
  const [tab1selectedChannels, settab1SelectedChannels] = useState([]);
  const [tab1selectedTerritories, settab1SelectedTerritories] = useState([]);

  // ========== API DATA STATE ==========
  /**
   * Main API response data containing:
   * - data: Array of records matching filter criteria
   * - product/channel/territory options
   * - combinations: Cascading relationships between dimensions
   */
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(false);                           // Ref to prevent race conditions
  const appliedViewIdRef = useRef(null);                      // Track applied viewid
  const [data, setData] = useState([]);                       // Full API response
  const [tabportfoliodata, setTabPortFolioData] = useState([]); // Portfolio data
  const [tabportfoliolicensordata, setTabPortFolioLicensorData] = useState([]); // Portfolio by licensor

  // ========== TABLE & FILTER OPTIONS STATE ==========
  /**
   * Options for dimension dropdowns (derived from API response)
   */
  const [tableData, setTableData] = useState([]);     // API data structure
  const [productOptions, setProductOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [territoryOptions, setTerritoryOptions] = useState([]);

  // ========== FILTER STATE ==========
  /**
   * Applied and temporary filter states
   * 
   * Structure:
   * {
   *   licensor: [{ value, label }],
   *   property: [{ value, label }],
   *   store: { value, label } | null,
   *   productL1/L2/L3: [{ value, label }],
   *   channelL1/L2/L3: [{ value, label }],
   *   territoryL1/L2: [{ value, label }],
   *   gender: [{ value, label }],
   *   ageGroup: [{ value, label }]
   * }
   */
  const [combinationFilters, setCombinationFilters] = useState({
    licensor: [],
    property: [],
    store: null,
    productL1: [],
    productL2: [],
    productL3: [],
    channelL1: [],
    channelL2: [],
    channelL3: [],
    territoryL1: [],
    territoryL2: [],
    gender: [],
    ageGroup: [],
  });
  
  /**
   * Temporary filters for preview before applying
   * Same structure as combinationFilters
   * Updated by filter selections, confirmed with Apply button
   */
  const [tempFilters, setTempFilters] = useState({
    licensor: [],
    property: [],
    store: null,
    productL1: [],
    productL2: [],
    productL3: [],
    channelL1: [],
    channelL2: [],
    channelL3: [],
    territoryL1: [],
    territoryL2: [],
    gender: [],
    ageGroup: [],
  });

  /**
   * Raw filter data from API
   * Used for cascading filter logic
   */
  const [rawFilterData, setRawFilterData] = useState(null);

  // ========== UI STATE - VIEW & NAVIGATION ==========
  /**
   * View mode: 'combination' for multi-dimensional filtering
   */
  const [view, setView] = useState('combination');
  const [step, setStep] = useState(1);
  
  // ========== SEARCH & PAGINATION STATE ==========
  /**
   * Search and pagination for combination results
   */
  const [combinationSearch, setCombinationSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 50;

  // ========== DIMENSION OPTIONS STATE ==========
  /**
   * Cache of dimension options (licensors, products, etc.)
   * Updated from API responses
   */
  const [licensors, setLicensors] = useState([]);
  const [properties, setProperties] = useState([]);
  const [products, setProducts] = useState([]);
  const [channels, setChannels] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [combinations, setCombinations] = useState([]);

  /**
   * Selected items from dropdown filters (for cascading logic)
   */
  const [selectedLicensor, setSelectedLicensor] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState([]);
  
  // ========== FILTER STRINGS ==========
  /**
   * Text filter inputs for dimension searches
   */
  const [licensorFilter, setLicensorFilter] = useState('');
  const [propertyFilter, setPropertyFilter] = useState('');
  const [productFilters, setProductFilters] = useState({ l1: '', l2: '', l3: '' });
  const [channelFilters, setChannelFilters] = useState({ l1: '', l2: '', l3: '' });
  const [territoryFilters, setTerritoryFilters] = useState({ l1: '', l2: '' });

  // ========== UI STATE - PANELS & MODALS ==========
  /**
   * Slide panel visibility states
   */
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showColumnsPanel, setShowColumnsPanel] = useState(false);
    
  /**
   * Save filter modal state
   */
  const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [activeFilterTab, setActiveFilterTab] = useState('create'); // 'create' or 'saved'
  const [savedFilters, setSavedFilters] = useState([]);
  const [expandedFilters, setExpandedFilters] = useState({});
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [selectedFilterId, setSelectedFilterId] = useState('');
  const [savingFilter, setSavingFilter] = useState(false);
  
  // ========== OTHER STATE ==========
  /**
   * File name and field selections
   */
  const [fileName, setFileName] = useState("");
  const [property, setProperty] = useState("");
  const [category, setCategory] = useState("");
  const [channel, setChannel] = useState("");
  const [territory, setTerritory] = useState("");

// ========== STYLE CONFIGURATION ==========
  /**
   * Common style for slide-out panels
   * Animates from right side with smooth transition
   * 
   * @param {boolean} visible - Whether panel should be visible
   * @returns {Object} CSS style object with fixed positioning and animation
   */
  const panelCommonStyle = (visible) => ({
    position: "fixed",
    top: "0px",
    right: visible ? 0 : -750,  // Slides in from right when visible
    width: "720px",
    height: "100vh",
    background: "#ffffff",
    boxShadow: "rgba(0, 0, 0, 0.12) 0px 6px 16px",
    transition: "right 220ms ease",  // Smooth animation
    zIndex: 1055,
    display: "flex",
    flexDirection: "column"
  });

  /* ========== EVENT HANDLERS - FILTER SELECTION ========== */
  
  /**
   * Handle licensor selection from dropdown
   * Updates tab1 state with selected licensors
   * Cascades to update available properties
   * 
   * @param {Array<Object>} selectedList - Selected licensor objects
   */
  const handleLicensorSelect = (selectedList) => {
    settab1SelectedLicensors(selectedList);
  };

  /**
   * Handle property selection from dropdown
   * Updates tab1 state with selected properties
   * 
   * @param {Array<Object>} selectedList - Selected property objects
   */
  const handlePropertiesSelect = (selectedList) => {
    settab1SelectedProperties(selectedList);
  };

  /**
   * Handle generic dimension selection
   * Updates state for any dimension filter
   * Resets pagination to page 1
   * 
   * @param {string} key - State key to update (e.g., 'licensors', 'products')
   * @param {Array<Object>} selectedList - Selected items
   */
  const handleSelect = (key, selectedList) => {
    setTab1(prev => ({ ...prev, [key]: selectedList }));
    setCurrentPage(1);
  };

  // ========== HELPER: EXTRACT FILTER VALUES ==========
  /**
   * Helper functions to safely extract values from filter objects
   * Handles missing/undefined filter fields
   */
  const licensors_tabportfolio = tabportfoliodata?.licensor || [];
  const productsportfolio = tabportfoliodata?.product || [];

  /* ========== API FUNCTIONS ==========
   * 
   * Functions for fetching and managing data
   */

  /**
   * Fetch explorer detail data from API
   * Retrieves filtered data, available dimensions, and valid combinations
   * 
   * API Endpoint: getDataExplorerDetails(filters)
   * 
   * Response structure:
   * {
   *   code: 200,
   *   data: {
   *     data: [{...records...}],  // Filtered data array
   *     product: [{...options...}],  // Product dimension options
   *     channel: [{...options...}],  // Channel dimension options
   *     territory: [{...options...}],  // Territory dimension options
   *     combinations: {
   *       licensor_property_combinations: [{licensor, propertyType}],
   *       product_combinations: [{l1, l2, l3}],
   *       channel_combinations: [{l1, l2, l3}],
   *       territory_combinations: [{l1, l2}],
   *       gender_age_combinations: [{gender, ageGroup}]
   *     }
   *   }
   * }
   * 
   * Performance Optimization:
   * - Polls DOM every 100ms to detect when table finishes rendering
   * - Waits up to 20 seconds for large dataset renders
   * - Uses requestAnimationFrame for final paint timing
   * 
   * @async
   * @param {Object} data - Filter parameters object
   * @returns {Promise<void>}
   */
  const getDataExplorerDetail = async (data) => {
    // Set both state and ref to track loading
    setLoading(true);
    loadingRef.current = true;
    
    try {
      // Call API with filter parameters (can take 10+ seconds for large results)
      const result = await getDataExplorerDetails(data);

      // Check if loading was cancelled
      if (!loadingRef.current) return;
      
      // Process API response only after complete JSON received
      if (result.code === API_SUCCESS_RESPONSE_CODE) {
        // Store API response with products/channels/territories and data array
        setTableData(result.data || {});
        setRawFilterData(result.data || {});  // Store for cascading logic
        setProductOptions(result.data?.product || []);
        setChannelOptions(result.data?.channel || []);
        setTerritoryOptions(result.data?.territory || []);

        // Clear loading immediately after API response
        setLoading(false);
        
        // Wait for table to render (large datasets can take 10+ seconds)
        const waitForTableRender = () => {
          let rafId;
          let timeoutId;
          let intervalId;
          let attempts = 0;
          const maxAttempts = 150;  // 15 seconds max (150 * 100ms)
          
          const checkTable = () => {
            // Check if loading was cancelled
            if (!loadingRef.current) {
              clearInterval(intervalId);
              clearTimeout(timeoutId);
              if (rafId) cancelAnimationFrame(rafId);
              return;
            }
            
            attempts++;
            // Poll DOM for table rows with data
            const tableBody = document.querySelector('.table-responsive tbody');
            const tableRows = tableBody ? tableBody.querySelectorAll('tr') : [];
            
            // Verify actual data rows exist (not just "no data" message)
            const hasDataRows = tableRows.length > 0 && 
                              tableRows[0].querySelectorAll('td').length > 1;
            
            // Verify rows have actual content (not just placeholders)
            const hasContent = hasDataRows && 
                             tableRows[0].textContent.trim().length > 0;
            
            if (hasContent) {
              // Found data rows with content - wait for final paint
              rafId = requestAnimationFrame(() => {
                clearInterval(intervalId);
                clearTimeout(timeoutId);
                loadingRef.current = false;
              });
            } else if (attempts >= maxAttempts) {
              // Safety timeout - give up after 15 seconds
              clearInterval(intervalId);
              clearTimeout(timeoutId);
              loadingRef.current = false;
              console.warn('Table render timeout after 15 seconds');
            }
          };
          
          // Poll DOM every 100ms for table content
          intervalId = setInterval(checkTable, 100);
          
          // Absolute safety timeout at 20 seconds
          timeoutId = setTimeout(() => {
            clearInterval(intervalId);
            if (rafId) cancelAnimationFrame(rafId);
            loadingRef.current = false;
            console.warn('Absolute timeout at 20 seconds');
          }, 20000);
        };
        
        // Trigger table render wait (async, doesn't block)
        waitForTableRender();
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
      loadingRef.current = false;
    }
  };

  // ========== EFFECTS - URL PARAMETERS & INITIALIZATION ==========
  
  /**
   * Initialize filters from URL parameters
   * Supports deep linking with type/filtervalue/viewid params
   * 
   * URL Parameter Format:
   *   /data-explorer-combination/{type}/{filtervalue}
   *   /data-explorer-combination/{type}/{filtervalue}/{viewid}/{viewname}
   * 
   * Type mapping:
   *   'licensor' → licensor filter
   *   'property' → property filter
   *   'productL1/L2/L3' → product hierarchy filter
   *   'channelL1/L2/L3' → channel hierarchy filter
   *   'territoryL1/L2' → territory hierarchy filter
   *   'gender', 'ageGroup' → demographic filters
   * 
   * Behavior:
   * - If viewid present: Skip URL filter init, load saved filter
   * - If viewid absent: Initialize filter from type/filtervalue
   * - Call API with constructed filter
   * 
   * @function useEffect
   * @dependency [type, filtervalue, viewid]
   */
  useEffect(() => {
    // Map URL type parameter to internal filter key
    const mapTypeToFilterKey = (urlType) => {
      const typeMapping = {
        'licensor': 'licensor',
        'propertyType': 'property',
        'property': 'property',
        'productL1': 'productL1',
        'Product Category (L1)': 'productL1',
        'productL2': 'productL2',
        'Product SubCategory (L2)': 'productL2',
        'productL3': 'productL3',
        'Product (L3)': 'productL3',
        'channelL1': 'channelL1',
        'Channel (L1)': 'channelL1',
        'channelL2': 'channelL2',
        'Channel SubType (L2)': 'channelL2',
        'channelL3': 'channelL3',
        'Channel (L3)': 'channelL3',
        'territoryL1': 'territoryL1',
        'Territory (L1)': 'territoryL1',
        'territoryL2': 'territoryL2',
        'Territory Zone (L2)': 'territoryL2',
        'gender': 'gender',
        'age_group': 'ageGroup',
        'ageGroup': 'ageGroup',
      };
      
      return typeMapping[urlType] || urlType;
    };

    // Build filter object from URL parameters
    const buildFilterFromParams = () => {
      const filterObj = {};
      
      // If type and filtervalue provided in URL, populate filter
      if (type && filtervalue) {
        const mappedType = mapTypeToFilterKey(type);
        filterObj[type] = [filtervalue];
      }
      
      return filterObj;
    };
    
    // Get filter object from URL params
    const filterParams = buildFilterFromParams();
    
    // Initialize filters from URL parameters if type/filtervalue present
    if (type && filtervalue) {
      const mappedType = mapTypeToFilterKey(type);
      const filterValue = { value: filtervalue, label: filtervalue };
      
      // Create filter object with URL-provided value
      const initialFilters = {
        licensor: mappedType === 'licensor' ? [filterValue] : [],
        property: mappedType === 'property' ? [filterValue] : [],
        store: mappedType === 'store' ? filterValue : null,
        productL1: mappedType === 'productL1' ? [filterValue] : [],
        productL2: mappedType === 'productL2' ? [filterValue] : [],
        productL3: mappedType === 'productL3' ? [filterValue] : [],
        channelL1: mappedType === 'channelL1' ? [filterValue] : [],
        channelL2: mappedType === 'channelL2' ? [filterValue] : [],
        channelL3: mappedType === 'channelL3' ? [filterValue] : [],
        territoryL1: mappedType === 'territoryL1' ? [filterValue] : [],
        territoryL2: mappedType === 'territoryL2' ? [filterValue] : [],
        gender: mappedType === 'gender' ? [filterValue] : [],
        ageGroup: mappedType === 'ageGroup' ? [filterValue] : [],
      };
      
      // Apply URL filters to both actual and temp filters
      setCombinationFilters(initialFilters);
      setTempFilters(initialFilters);
    }
    
    // Only apply type/filtervalue if viewid is NOT in URL
    // (viewid takes precedence, will be handled by separate useEffect)
    if (!viewid) {
      // Call API with constructed filter parameters
      getDataExplorerDetail(filterParams);
    }
   }, [type, filtervalue, viewid]);

   /**
    * Handle saved filter loading from viewid URL parameter
    * Triggered when user navigates from saved filters list
    * 
    * Flow:
    * 1. Check if viewid matches previously applied viewid (prevent duplicates)
    * 2. Wait for savedFilters to load from API
    * 3. Find filter object matching viewid
    * 4. Apply saved filter (sets filters and calls API)
    * 5. Mark viewid as applied to prevent re-application
    * 
    * @function useEffect
    * @dependency [viewid, savedFilters]
    */
   useEffect(() => {
    if (viewid) {
      // Skip if this viewid already applied
      if (appliedViewIdRef.current === viewid) {
        return;
      }

      // Wait for savedFilters to load, then apply matching filter
      if (savedFilters.length > 0) {
        // Find saved filter matching viewid (compare as strings)
        const filterToApply = savedFilters.find(f => String(f.id) === String(viewid));
        
        if (filterToApply) {
          // Update dropdown selection to show selected filter
          setSelectedFilterId(viewid);
          // Apply the saved filter (updates filters and calls API)
          handleApplySavedFilter(filterToApply);
          // Mark viewid as applied to prevent re-application
          appliedViewIdRef.current = viewid;
          
        } else {
          console.warn(`No filter found with id: ${viewid}`);
        }
      }
      // If savedFilters are not loaded yet and not already loading, load them
      // else if (!loadingFilters) {
      //   loadSavedFilters();
      // }
    } else {
      // Reset when viewid is cleared
      appliedViewIdRef.current = null;
    }
  }, [viewid, savedFilters]);


  const filteredLicensors = licensors.filter(l =>
    l.name.toLowerCase().includes(licensorFilter.toLowerCase())
  );

  const filteredProperties = properties.filter(p =>
    p.name.toLowerCase().includes(propertyFilter.toLowerCase())
  );

  const uniqueProducts = {
    level1: [...new Set(products.map(p => p.level1))],
    level2: [...new Set(products.map(p => p.level2))],
    level3: [...new Set(products.map(p => p.level3))],
  };

  const uniqueChannels = {
    level1: [...new Set(channels.map(c => c.level1))],
    level2: [...new Set(channels.map(c => c.level2))],
    level3: [...new Set(channels.map(c => c.level3))],
  };

  const uniqueTerritories = {
    level1: [...new Set(territories.map(t => t.level1))],
    level2: [...new Set(territories.map(t => t.level2))],
  };

  const filteredProducts = products.filter(p =>
    p.level1.toLowerCase().includes(productFilters.l1.toLowerCase()) &&
    p.level2.toLowerCase().includes(productFilters.l2.toLowerCase()) &&
    p.level3.toLowerCase().includes(productFilters.l3.toLowerCase())
  );

  const filteredChannels = channels.filter(c =>
    c.level1.toLowerCase().includes(channelFilters.l1.toLowerCase()) &&
    c.level2.toLowerCase().includes(channelFilters.l2.toLowerCase()) &&
    c.level3.toLowerCase().includes(channelFilters.l3.toLowerCase())
  );

  const filteredTerritories = territories.filter(t =>
    t.level1.toLowerCase().includes(territoryFilters.l1.toLowerCase()) &&
    t.level2.toLowerCase().includes(territoryFilters.l2.toLowerCase())
  );

  // Process API data to extract licensors and properties
  const processApiData = (apiData) => {
    const processedLicensors = apiData.map(item => ({
      id: item.label, // Using label as ID since it's unique
      name: item.label,
      property_count: item.propertyType.length
    })).sort((a, b) => a.name.localeCompare(b.name));

    // Extract all properties from all licensors
    const allProperties = [];
    apiData.forEach(licensor => {
      licensor.propertyType.forEach(property => {
        allProperties.push({
          id: `${licensor.label}_${property}`, // Create unique ID
          licensor_id: licensor.label,
          name: property
        });
      });
    });

    return { processedLicensors, allProperties };
  };

  const fetchDataTab2 = async () => {
    try {
      const result = await fetchTaxonomyData();
      if (result.code === API_SUCCESS_RESPONSE_CODE) {
        
      } else {
        console.warn("Unexpected response status:", result.status);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      trackError(error);
    } finally {
      //setLoading(false);
    }
  };

  useEffect(() => {
    fetchDataTab2();
  }, []);

  // Helper function to prepare filter parameters for API call
  const prepareFilterParams = (filters) => {
    const filterParams = {};
    if (filters.licensor.length > 0) {
      filterParams.licensor = filters.licensor.map(item => item.label);
    }
    if (filters.property.length > 0) { // Add this block
      filterParams.propertyType = filters.property.map(item => item.label);
    }
    if (filters.store) {
      filterParams.store = filters.store.label;
    }
    if (filters.productL1.length > 0) {
      filterParams.productL1 = filters.productL1.map(item => item.label);
    }
    if (filters.productL2.length > 0) {
      filterParams.productL2 = filters.productL2.map(item => item.label);
    }
    if (filters.productL3.length > 0) {
      filterParams.productL3 = filters.productL3.map(item => item.label);
    }
    if (filters.channelL1.length > 0) {
      filterParams.channelL1 = filters.channelL1.map(item => item.label);
    }
    if (filters.channelL2.length > 0) {
      filterParams.channelL2 = filters.channelL2.map(item => item.label);
    }
    if (filters.channelL3.length > 0) {
      filterParams.channelL3 = filters.channelL3.map(item => item.label);
    }
    if (filters.territoryL1.length > 0) {
      filterParams.territoryL1 = filters.territoryL1.map(item => item.label);
    }
    if (filters.territoryL2.length > 0) {
      filterParams.territoryL2 = filters.territoryL2.map(item => item.label);
    }
    if (filters.gender.length > 0) {
      filterParams.gender = filters.gender.map(item => item.label);
    }
    if (filters.ageGroup.length > 0) {
      filterParams.age_group = filters.ageGroup.map(item => item.label);
    }
    return filterParams;
  };

  // Update the licensorProperties calculation to work with API data
  const licensorProperties = properties.filter(p =>
    selectedLicensor ? p.licensor_id === selectedLicensor.id : false
  );

  const toggleProperty = (propertyName) => {
    if (selectedProperties.includes(propertyName)) {
      setSelectedProperties(selectedProperties.filter(p => p !== propertyName));
    } else {
      setSelectedProperties([...selectedProperties, propertyName]);
    }
  };

  const getRelevantCombinations = () => {
    // Check if we have tableData from the API
    if (tableData && tableData.data && Array.isArray(tableData.data) && tableData.data.length > 0) {
      // Directly map the data array from API response
      return tableData.data.map((item, index) => ({
        id: `${item.licensor}_${item.propertyType}_${item["Product Category (L1)"]}_${item["Channel (L1)"]}_${item["Territory (L1)"]}_${item.gender}_${item.age_group}_${index}`,
        licensor: item.licensor || '',
        property: item.propertyType,
        product_l1: item["Product Category (L1)"] || '',
        product_l2: item["Product SubCategory (L2)"] || '',
        product_l3: item["Product (L3)"] || '',
        channel_l1: item["Channel (L1)"] || '',
        channel_l2: item["Channel SubType (L2)"] || '',
        channel_l3: item["Channel (L3)"] || '',
        territory: item["Territory"] || '',
        territory_l1: item["Territory (L1)"] || '',
        territory_l2: item["Territory Zone (L2)"] || '',
        gender: item.gender || '',
        age_group: item.age_group || '',
        exceptions: item.exceptions || ''
      }));
    }

    

    // Return empty array if no data
    return [];
  };

  // Add state for sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Add sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Add sort icon component
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.3, marginLeft: '4px' }}>
          <path d="m7 15 5 5 5-5"/>
          <path d="m7 9 5-5 5 5"/>
        </svg>
      );
    }
    
    if (sortConfig.direction === 'asc') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '4px' }}>
          <path d="m7 15 5 5 5-5"/>
        </svg>
      );
    }
    
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '4px' }}>
        <path d="m7 9 5-5 5 5"/>
      </svg>
    );
  };

  const filteredCombinations = (() => {
    // First apply filters and search
    const filtered = getRelevantCombinations().filter(c => {
      // const matchesFilters =
      //   (combinationFilters.licensor.length === 0 || combinationFilters.licensor.some(f => f.value === c.licensor)) &&
      //   (combinationFilters.property.length === 0 || combinationFilters.property.some(f => f.value === c.property)) &&
      //   (combinationFilters.productL1.length === 0 || combinationFilters.productL1.some(f => f.value === c.product_l1)) &&
      //   (combinationFilters.productL2.length === 0 || combinationFilters.productL2.some(f => f.value === c.product_l2)) &&
      //   (combinationFilters.productL3.length === 0 || combinationFilters.productL3.some(f => f.value === c.product_l3)) &&
      //   (combinationFilters.channelL1.length === 0 || combinationFilters.channelL1.some(f => f.value === c.channel_l1)) &&
      //   (combinationFilters.channelL2.length === 0 || combinationFilters.channelL2.some(f => f.value === c.channel_l2)) &&
      //   (combinationFilters.channelL3.length === 0 || combinationFilters.channelL3.some(f => f.value === c.channel_l3)) &&
      //   // (combinationFilters.territoryL1.length === 0 || combinationFilters.territoryL1.some(f => f.value === c.territory_l1)) &&
      //   // (combinationFilters.territoryL2.length === 0 || combinationFilters.territoryL2.some(f => f.value === c.territory_l2)) &&
      //   (combinationFilters.gender.length === 0 || combinationFilters.gender.some(f => f.value === c.gender)) &&
      //   (combinationFilters.ageGroup.length === 0 || combinationFilters.ageGroup.some(f => f.value === c.age_group));

      const matchesSearch = combinationSearch === '' ||
        Object.values(c).some(val =>
          String(val).toLowerCase().includes(combinationSearch.toLowerCase())
        );

     // return matchesFilters && matchesSearch;

      return  matchesSearch;
    });

    // Then apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        
        // Handle string comparison
        const comparison = String(aValue).localeCompare(String(bValue), undefined, { numeric: true });
        
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }

    return filtered;
  })();

  const totalPages = Math.ceil(filteredCombinations.length / rowsPerPage);
  const paginatedCombinations = filteredCombinations.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  /* ========== CASCADING FILTER LOGIC ==========
   * 
   * Prevents invalid filter combinations by using API-provided combinations data
   * Each hierarchy shows only values that exist in combination with parent selections
   * 
   * Filter Logic:
   * 1. Extract selected values from tempFilters
   * 2. Get combinations data from API response
   * 3. Filter combinations based on parent selections
   * 4. Extract unique values from filtered combinations
   * 5. Return as array of {value, label} objects for dropdown
   */
  
  // ========== EXTRACT SELECTED VALUES ==========
  /**
   * Map selected filter objects to their values for cascading logic
   * Filter objects format: [{value: "...", label: "..."}]
   */
  const selectedLicensorValues = tempFilters.licensor.map(f => f.value);
  const selectedPropertyValues = tempFilters.property.map(f => f.value);
  const selectedProductL1 = tempFilters.productL1.map(f => f.value);
  const selectedProductL2 = tempFilters.productL2.map(f => f.value);
  const selectedChannelL1 = tempFilters.channelL1.map(f => f.value);
  const selectedChannelL2 = tempFilters.channelL2.map(f => f.value);
  const selectedTerritoryL1 = tempFilters.territoryL1.map(f => f.value);
  const selectedTerritoryL2 = tempFilters.territoryL2.map(f => f.value);

  // ========== COMBINATIONS DATA ==========
  /**
   * Extract cascading relationships from API response
   * Each combinations array contains valid relationships between dimensions
   */
  const productCombinations = tableData?.combinations?.product_combinations || [];
  const channelCombinations = tableData?.combinations?.channel_combinations || [];
  const territoryCombinations = tableData?.combinations?.territory_combinations || [];

  /**
   * Convert array of combination objects to unique option list
   * Eliminates duplicates, sorts alphabetically, and formats for dropdown
   * 
   * @param {Array<Object>} arr - Array of combination objects
   * @param {string} key - Property name to extract (e.g., 'l1', 'l2', 'licensor')
   * @returns {Array<{value: string, label: string}>} Sorted unique options
   */
  const getUniqueOptions = (arr, key) => {
    return Array.from(new Set(arr.map(c => c[key]).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({ value: v, label: v }));
  };

  /**
   * Calculate cascading filter options based on parent selections
   * 
   * Memoized to prevent recalculation on every render
   * Dependencies: All selected filter values + combinations data + table data
   * 
   * For each dimension:
   * - If no parent selected: show all options
   * - If parent selected: show only options associated with parent
   * - If multi-parent (L1+L2): show options for more specific parent
   * 
   * Returns object with keys: licensor, property, productL1/L2/L3, 
   *                         channelL1/L2/L3, territoryL1/L2, gender, ageGroup
   * Each value is array of {value, label} objects for dropdown rendering
   */
  const uniqueCombinationValues = useMemo(() => ({
    licensor: getUniqueOptions(tableData?.combinations?.licensor_property_combinations || [], 'licensor'),
    property: (() => {
      if (selectedLicensorValues.length === 0) {
        return getUniqueOptions(tableData?.combinations?.licensor_property_combinations || [], 'propertyType');
      }
      // Only show properties associated with selected licensors
      const filtered = (tableData?.combinations?.licensor_property_combinations || [])
        .filter(c => selectedLicensorValues.includes(c.licensor));

        // console.log("-------------")
        // console.log(selectedLicensorValues)
        // console.log(filtered)
        // console.log("-------------")

      return getUniqueOptions(filtered, 'propertyType');
    })(),
    productL1: (() => {
      // If L1 is selected, only show those in dropdown
      if (selectedProductL1.length > 0) {
        return getUniqueOptions(productCombinations.filter(c => selectedProductL1.includes(c.l1)), 'l1');
      }
      return getUniqueOptions(productCombinations, 'l1');
    })(),
    productL2: (() => {
      if (selectedProductL1.length === 0) {
        return getUniqueOptions(productCombinations, 'l2');
      }
      // Only show L2s associated with selected L1(s)
      const filtered = productCombinations.filter(c => selectedProductL1.includes(c.l1));
      return getUniqueOptions(filtered, 'l2');
    })(),
    productL3: (() => {
      if (selectedProductL2.length > 0) {
        // When L2 is selected, show L3 associated with L2
        const filtered = productCombinations.filter(c => selectedProductL2.includes(c.l2));
        return getUniqueOptions(filtered, 'l3');
      }
      if (selectedProductL1.length > 0) {
        // When L1 is selected but L2 is not, show L3 associated with L1
        const filtered = productCombinations.filter(c => selectedProductL1.includes(c.l1));
        return getUniqueOptions(filtered, 'l3');
      }
      return getUniqueOptions(productCombinations, 'l3');
    })(),
    channelL1: getUniqueOptions(channelCombinations, 'l1'),
    channelL2: (() => {
      if (selectedChannelL1.length === 0) {
        return getUniqueOptions(channelCombinations, 'l2');
      }
      // Only show L2s associated with selected L1(s)
      const filtered = channelCombinations.filter(c => selectedChannelL1.includes(c.l1));
      return getUniqueOptions(filtered, 'l2');
    })(),
    channelL3: (() => {
      if (selectedChannelL2.length > 0) {
        // When L2 is selected, show L3 associated with L2
        const filtered = channelCombinations.filter(c => selectedChannelL2.includes(c.l2));
        return getUniqueOptions(filtered, 'l3');
      }
      if (selectedChannelL1.length > 0) {
        // When L1 is selected but L2 is not, show L3 associated with L1
        const filtered = channelCombinations.filter(c => selectedChannelL1.includes(c.l1));
        return getUniqueOptions(filtered, 'l3');
      }
      return getUniqueOptions(channelCombinations, 'l3');
    })(),
    territory: getUniqueOptions(territoryCombinations, 'territory'),
    territoryL1: getUniqueOptions(territoryCombinations, 'l1'),
    territoryL2: (() => {
      if (selectedTerritoryL1.length === 0) {
        return getUniqueOptions(territoryCombinations, 'l2');
      }
      // Only show L2s associated with selected L1(s)
      const filtered = territoryCombinations.filter(c => selectedTerritoryL1.includes(c.l1));
      return getUniqueOptions(filtered, 'l2');
    })(),
    gender: getUniqueOptions(tableData?.combinations?.gender_age_combinations || [], 'gender'),
    ageGroup: getUniqueOptions(tableData?.combinations?.gender_age_combinations || [], 'ageGroup'),
  }), [selectedLicensorValues, selectedPropertyValues, selectedProductL1, selectedProductL2, selectedChannelL1, selectedChannelL2, selectedTerritoryL1, selectedTerritoryL2, tableData, productCombinations, channelCombinations, territoryCombinations]);




  // ========== COLUMN VISIBILITY STATE ==========
  /**
   * Track which table columns are visible/hidden
   * User can toggle columns via columns slide panel
   * Persists across filter changes
   * 
   * Format: { columnName: boolean }
   * Example: { "Licensor": true, "Property": false, "Channel": true }
   */
  // Add state for visible columns
  const [visibleColumns, setVisibleColumns] = useState({
    "Licensor": true,
    "Property": true,
    "Product L1": true,
    "Product L2": true,
    "Product L3": true,
    "Channel L1": true,
    "Channel L2": true,
    "Channel L3": true,
    // "Territory L1": true,
    // "Territory L2": true,
    "Territory": true,
    "Gender": true,
    "Age Group": true,
    "Exception": true,
  });

  // Temporary state for column changes before applying
  const [tempVisibleColumns, setTempVisibleColumns] = useState({...visibleColumns});

  // Modal state for showing exception details
  const [showExceptionModal, setShowExceptionModal] = useState(false);
  const [exceptionContent, setExceptionContent] = useState('');

  const openExceptionModal = (content) => {
    setExceptionContent(content || '');
    setShowExceptionModal(true);
  };

  const closeExceptionModal = () => {
    setShowExceptionModal(false);
    setExceptionContent('');
  };

  // Add handler for column toggle (updates temp state)
  const handleColumnToggle = (columnName, isVisible) => {
    setTempVisibleColumns(prev => ({
      ...prev,
      [columnName]: isVisible
    }));
  };

  // Apply column changes
  const applyColumnChanges = () => {
    setVisibleColumns({...tempVisibleColumns});
    setShowColumnsPanel(false);
  };

  // Cancel column changes
  const cancelColumnChanges = () => {
    setTempVisibleColumns({...visibleColumns});
    setShowColumnsPanel(false);
  };

  // When opening columns panel, sync temp state
  const openColumnsPanel = () => {
    setTempVisibleColumns({...visibleColumns});
    setShowColumnsPanel(true);
    setShowFilterPanel(false);
  };

  // Check if any filters are applied
  const hasAppliedFilters = () => {
    return Object.values(combinationFilters).some(val => 
      Array.isArray(val) ? val.length > 0 : val !== null
    );
  };

  // Load saved filters from API
  const loadSavedFilters = async () => {
    setLoadingFilters(true);
    try {
      const response = await apiCall("GET", API_SERVER_URL + "/agent/views/");
      
      console.log("Load Saved Filters Response:", response);
      
      if (response.code === API_SUCCESS_RESPONSE_CODE && response.data) {
        // Convert API response to array format
        const filtersArray = Array.isArray(response.data) ? response.data : [];
        setSavedFilters(filtersArray);
      }
    } catch (error) {
      console.error("Error loading saved filters:", error);
      // Fallback to empty array on error
      setSavedFilters([]);
    } finally {
      setLoadingFilters(false);
    }
  };

  // Handle save filter
  const handleSaveFilter = async () => {
    if (!filterName.trim()) {
      alert('Please enter a filter name');
      return;
    }

    const payload = {
      name: filterName,
      filters: combinationFilters,
      showColumns: tempVisibleColumns // Use tempVisibleColumns instead of visibleColumns
    };

    console.log('Saving Filter:', payload);
    
    setSavingFilter(true); // Start loading
    try {
      const response = await apiCall("POST", API_SERVER_URL + "/agent/views/", payload);
      
      console.log("Save Filter Response:", response);
      
      if (response.code === API_SUCCESS_RESPONSE_CODE) {
       // alert(`Filter "${filterName}" saved successfully!`);
        setFilterName('');
        setActiveFilterTab('saved');
        loadSavedFilters();
        setShowSaveFilterModal(false);

      } else {
        alert('Failed to save filter. Please try again.');
      }
    } catch (error) {
      console.error("Error saving filter:", error);
      alert('Error saving filter: ' + error.message);
    } finally {
      setSavingFilter(false); // Stop loading indicator
    }
  };

  /* ========== FILTER APPLICATION HANDLERS ==========
   * 
   * Functions to apply and manage saved filters
   */

  /**
   * Apply a saved filter to the current view
   * 
   * Actions:
   * 1. Set combination and temp filters to saved filter values
   * 2. Restore saved column visibility if available
   * 3. Reset pagination to page 1
   * 4. Close save modal
   * 5. Call API with filter parameters
   * 
   * @param {Object} savedFilter - Saved filter object with structure:
   *   {
   *     id: number,
   *     name: string,
   *     filters: {...},
   *     showColumns: {...} (optional),
   *     created_at: ISO string
   *   }
   * @returns {void}
   */
  const handleApplySavedFilter = (savedFilter) => {
    // Apply filter values to both active and temp filters
    setCombinationFilters(savedFilter.filters);
    setTempFilters(savedFilter.filters);
    
    // Restore column visibility if saved with filter
    if (savedFilter.showColumns) {
      setVisibleColumns(savedFilter.showColumns);
      setTempVisibleColumns(savedFilter.showColumns);
    }
    
    // Reset pagination
    setCurrentPage(1);
    // Close modal
    setShowSaveFilterModal(false);
    
    // Fetch data with saved filter parameters
    getDataExplorerDetail(prepareFilterParams(savedFilter.filters));
  };

  /**
   * Delete a saved filter by ID
   * Prompts user for confirmation before deletion
   * Reloads saved filters list after successful deletion
   * 
   * API Endpoint: DELETE /agent/views/{filterId}/
   * 
   * @async
   * @param {number|string} filterId - ID of filter to delete
   * @returns {Promise<void>}
   */
  const handleDeleteSavedFilter = async (filterId) => {
    // Confirm deletion with user
    if (!confirm('Are you sure you want to delete this saved filter?')) {
      return;
    }

    try {
      // Call DELETE API endpoint to remove saved filter
      const response = await apiCall("DELETE", API_SERVER_URL + `/agent/views/${filterId}/`);
      
      console.log("Delete Filter Response:", response);
      
      if (response.code === API_SUCCESS_RESPONSE_CODE) {
        // Reload saved filters list from API
        loadSavedFilters();
        alert('Filter deleted successfully!');
      } else {
        alert('Failed to delete filter. Please try again.');
      }
    } catch (error) {
      console.error("Error deleting filter:", error);
      alert('Error deleting filter: ' + error.message);
    }
  };

  /* ========== TABLE HEIGHT CALCULATION ==========
   * 
   * Dynamically calculate table height to fill viewport
   * Accounts for header, filter panel, and pagination controls
   */

  /**
   * Refs for height calculation elements
   */
  // Add state for dynamic height
const [tableHeight, setTableHeight] = useState('calc(100vh - 440px)');
const tableContainerRef = useRef(null);
const headerRef = useRef(null);

/**
 * Calculate table height based on elements above it
 * Triggered on mount and resize events
 * 
 * Logic:
 * 1. Get heights of header, filter panel, pagination
 * 2. Subtract from viewport height
 * 3. Set remaining space as table height
 * 
 * @function useEffect
 * @dependency []
 */
// Calculate table height based on elements above it
useEffect(() => {
  loadSavedFilters()
  const calculateTableHeight = () => {
    // Get the heights of all elements above the table
    const pageContentPadding = 0; // Adjust if there's padding
    const containerPadding = 0; // Adjust if there's padding
    
    // Get the header section height
    const headerElement = document.querySelector('.mb-1');
    const breadcrumbElement = document.querySelector('.breadcrumb');
    const actionBarElement = document.querySelector('.btn-group')?.closest('.d-flex');
    const appliedFiltersElement = document.querySelector('.card.border-0.shadow-sm.mb-3');
    const statsElement = document.querySelector('.d-flex.justify-content-between.align-items-center.mb-1');
    
    let totalHeightAbove = 0;
    
    if (headerElement) totalHeightAbove += headerElement.offsetHeight;
    if (breadcrumbElement) totalHeightAbove += breadcrumbElement.offsetHeight;
    if (actionBarElement) totalHeightAbove += actionBarElement.offsetHeight;
    if (appliedFiltersElement) totalHeightAbove += appliedFiltersElement.offsetHeight;
    if (statsElement) totalHeightAbove += statsElement.offsetHeight;
    
    // Add margins and padding (approximate)
    const margins = 60; // Adjust based on your actual margins
    
    // Calculate available height
    const windowHeight = window.innerHeight;
    const availableHeight = windowHeight - totalHeightAbove - margins;
    
    // Set minimum height
    const minHeight = 400;
    const calculatedHeight = Math.max(availableHeight, minHeight);
    
    //setTableHeight(`${calculatedHeight}px`);
    setTableHeight($(window).height() - 450 + 'px')
  };

  // Calculate on mount and when window resizes
  calculateTableHeight();
  window.addEventListener('resize', calculateTableHeight);
  
  // Recalculate when filters change (elements above might change height)
  const timer = setTimeout(calculateTableHeight, 100);
  
  return () => {
    window.removeEventListener('resize', calculateTableHeight);
    clearTimeout(timer);
  };
}, [combinationFilters, showFilterPanel, showColumnsPanel]); // Recalculate when these change

// alert(tableHeight);

// alert($(window).height());


// Update the tableContainerStyle to use dynamic height
const tableContainerStyle = {
  maxHeight: tableHeight,
  minHeight: '400px',
  overflowY: 'scroll',
  overflowX: 'scroll',
  position: 'relative',
  border: '1px solid #dee2e6',
  borderRadius: '8px',
  backgroundColor: '#fff',
  paddingBottom: '10px',
  display: 'block'
};

  // Add this useEffect to control body scroll
  useEffect(() => {
    // document.body.style.overflow = 'hidden';
    // document.documentElement.style.overflow = 'hidden';
    
    // return () => {
    //   document.body.style.overflow = 'auto';
    //   document.documentElement.style.overflow = 'auto';
    // };
  }, []);

  // Add sticky header style
const stickyHeaderStyle = {
  position: 'sticky',
  top: 0,
  backgroundColor: '#f8f9fa',
  zIndex: 10,
  fontSize: '11px',
  fontWeight: '600',
  padding: '8px',
  borderBottom: '2px solid #dee2e6',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  cursor: 'pointer',
  userSelect: 'none',
};

// Add cell style
const cellStyle = {
  fontSize: '11px',
  padding: '6px 8px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  borderBottom: '1px solid #f0f0f0'
};

return (
  <div className="main-content" style={{ height: '100vh', overflowY: 'hidden' }}>
      <div className="page-content" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div className="container-fluid" style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>

             <div className="mb-1">
            <div className="d-flex align-items-center mb-2">
              <div className="bg-primary text-white rounded p-2 me-3">
                <CommonIcon iconName="Telescope" size={"24px"}  color="#fff" />
              </div>
              <div className="flex-grow-1">
                <h1 className="h3 mb-0 fw-bold">Data Explorer</h1>
                <p className="text-muted mb-0 small">Explore and manage your business data</p>
              </div>
     <div className="ms-auto bg-primary" style={{padding:"1px",border:"1px solid #000",position:"absolute", right:"5px",borderRadius:"2px",top:"15px"}}>
    <img src="../images/predatica_logo.png" alt="Logo" height={"20px"}  /> 
    </div>
            </div>  

          </div>

          <nav aria-label="breadcrumb">
  <ol className="breadcrumb bg-white p-2 rounded">
    <span onClick={() => navigate("/data-explorer")}   style={{ color: 'blue', cursor: 'pointer' }}
>
        ← Back 
      </span> &nbsp;&nbsp;
    <li className="breadcrumb-item">
      <span><i className="fa fa-home me-1"></i>{type ? type.replace(/Search\s*/gi, '') : 'Saved View'}</span> <svg
  xmlns="http://www.w3.org/2000/svg"
  width="15"
  height="15"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="lucide lucide-chevron-right-icon lucide-chevron-right"
>
  <path d="m9 18 6-6-6-6" />
</svg>
{filtervalue || (viewid ? ` ${viewname}` : '')}
    </li>
    {/* <li className="breadcrumb-item">
      <span>Properties</span> <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-right-icon lucide-chevron-right"><path d="m9 18 6-6-6-6"/></svg>
    </li> 
    <li className="breadcrumb-item" aria-current="page">
      
    </li> */}
<li className="text-center ">
     <div className="flex-grow-1" style={{ height: 28, padding: 1,right: '10px',position: 'absolute'}}>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <strong className="me-1">Note</strong>
            <span className="text-muted">
              — Only <span className="fw-semibold">5000</span> records are displayed in this grid. To view more records, refine your filters
            </span>
          </div>
        </div>
        <div className="mt-1 small text-muted d-none">
          To view more records, refine your filters or export the full dataset.
        </div>
      </div>
</li>
  </ol>

 

</nav>

    
    

          { loading ? <PageLoader /> :
      
          hasPageAccess(pageName, "view") === false ? accessDenied() : 
          (
          <>    

     {view === 'combination' && (
            <div>





              <div className="d-flex align-items-center mb-3 gap-3 ">
        <div className=" d-none btn-group flex-shrink-0" role="group" aria-label="actions" style={{ minWidth: 'fit-content' }}>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm d-flex align-items-center"
            onClick={() => {
      const columnsOrder = [
  "Licensor",
  "Property",
  "Product L1",
  "Product L2",
  "Product L3",
  "Channel L1",
  "Channel L2",
  "Channel L3",
  "Territory",
  "Territory L1",
  "Territory L2",
  "Gender",
  "Age Group",
  "Exception"
];

const visibleCols = columnsOrder.filter(col => visibleColumns[col]);

const rows = filteredCombinations.map(c => {
  const row = {};
  visibleCols.forEach(col => {
    switch (col) {
      case "Licensor":
        row[col] = c.licensor || '';
        break;
      case "Property":
        row[col] = c.property;
        break;
      case "Product L1":
        row[col] = c.product_l1;
        break;
      case "Product L2":
        row[col] = c.product_l2;
        break;
      case "Product L3":
        row[col] = c.product_l3 || '';
        break;
      case "Channel L1":
        row[col] = c.channel_l1;
        break;
      case "Channel L2":
        row[col] = c.channel_l2;
        break;
      case "Channel L3":
        row[col] = c.channel_l3 || '';
        break;
      case "Territory":
        row[col] = c.territory || c["Territory"] || c.territory_l1 || c["Territory L1"] || c.territory_l2 || c["Territory L2"] || '';
        break;
      case "Territory L1":
        row[col] = c.territory_l1 || c["Territory L1"] || '';
        break;
      case "Territory L2":
        row[col] = c.territory_l2 || c["Territory L2"] || '';
        break;
      case "Gender":
        row[col] = c.gender || '';
        break;
      case "Age Group":
        row[col] = c.age_group || '';
        break;
      case "Exception":
        row[col] = c.exceptions || 'N/A';
        break;
      default:
        row[col] = '';
    }
  });
  return row;
});

if (!rows.length) {
  alert('No data to export');
  return;
}

// ✅ CSV is safer than TSV for Excel (especially on Windows)
const header = Object.keys(rows[0]);

const escapeCell = (v) => {
  if (v == null) return '';
  let cleaned = String(v)
    .replace(/\r?\n/g, ' ')   // Remove newlines
    .replace(/\|/g, '; ')     // Replace pipes
    .trim();
  return '"' + cleaned.replace(/"/g, '""') + '"';
};

// Use comma-separated and add UTF-8 BOM
const csv = '\uFEFF' + header.map(h => '"' + h + '"').join(',') + '\n' +
  rows.map(r => header.map(h => escapeCell(r[h])).join(',')).join('\n');

// ✅ Use .csv extension for Excel
const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
const url = URL.createObjectURL(blob);
const a = document.createElement('a');
const nameBase = (selectedLicensor?.name || 'combinations').replace(/\s+/g, '_');
a.href = url;
a.download = `${nameBase}_${new Date().toISOString().slice(0, 10)}.csv`;
document.body.appendChild(a);
a.click();
a.remove();
URL.revokeObjectURL(url);

    }}
          >
            <ListChecks size={16} className="me-2" /> Export 
          </button>

          <button
            type="button"
            className="btn btn-outline-primary btn-sm d-flex align-items-center"
            onClick={() => {
              // Sync temp filters with current filters when opening panel
              if (!showFilterPanel) {
                setTempFilters({...combinationFilters});
                setShowColumnsPanel(false);
              }
              setShowFilterPanel(v => !v);
            }}
            title="Filter"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-funnel-icon lucide-funnel"><path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z"/></svg> Filter 
          </button>
      
            {/* <button
              type="button"
              className="btn btn-outline-primary btn-sm d-flex align-items-center"
              onClick={() => {
                // Load saved filters and show "Already Saved Filters" tab
                loadSavedFilters();
                setActiveFilterTab('saved');
                setTempVisibleColumns({...visibleColumns}); // Sync temp columns with current visible columns
                setShowSaveFilterModal(true);
              }}
              title="Save current filter"
              style={{ whiteSpace: 'nowrap' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                <polyline points="17 21 17 13 7 13 7 21"/>
                <polyline points="7 3 7 8 15 8"/>
              </svg>
              Save Filter
            </button> */}
       

          <button
            type="button"
            className="d-none btn btn-outline-primary btn-sm d-flex align-items-center"
            onClick={openColumnsPanel}
            title="Columns"
          >
            <Layers size={16} className="me-2" /> Columns 
          </button>
        </div>

        <div className="d-flex align-items-center gap-2 flex-shrink-0">
  {/* Export Icon */}

  {/* <button
    type="button"
    className="btn btn-outline-primary btn-sm d-flex align-items-center justify-content-center"
    title="Export All Data"
    onClick={() => {
      // Inform user via a Bootstrap toast that the file will be emailed
      try {
        const msg = 'The download link for the file will be sent to your email address.”';
        const containerId = 'dynamic-toast-container';
        let container = document.getElementById(containerId);
        if (!container) {
          container = document.createElement('div');
          container.id = containerId;
          container.className = 'toast-container position-fixed bottom-0 end-0 p-3';
          document.body.appendChild(container);
        }

        const toastEl = document.createElement('div');
        toastEl.className = 'toast';
        toastEl.setAttribute('role', 'alert');
        toastEl.setAttribute('aria-live', 'assertive');
        toastEl.setAttribute('aria-atomic', 'true');
        toastEl.innerHTML = `
          <div class="toast-header">
            <strong class="me-auto">Info</strong>
            <small class="text-muted">Just now</small>
            <button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close"></button>
          </div>
          <div class="toast-body">${msg}</div>
        `;

        container.appendChild(toastEl);
        const toast = new window.bootstrap.Toast(toastEl, { delay: 3500 });
        toast.show();
        toastEl.addEventListener('hidden.bs.toast', () => {
          try { toastEl.remove(); } catch (e) {}
          if (container && container.children.length === 0) {
            try { container.remove(); } catch (e) {}
          }
        });
      } catch (e) {
        alert('The download link for the file will be sent to your email address.”');
      }
    }}
    >
<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-cloud-download-icon lucide-cloud-download"><path d="M12 13v8l-4-4"/><path d="m12 21 4-4"/><path d="M4.393 15.269A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.436 8.284"/></svg>

    </button> */}
  <button
    type="button"
    className="btn btn-outline-primary btn-sm d-flex align-items-center justify-content-center"
    title="Export Current View"
    onClick={() => {
      const columnsOrder = [
        "Licensor",
        "Property",
        "Product L1",
        "Product L2",
        "Product L3",
        "Channel L1",
        "Channel L2",
        "Channel L3",
        "Territory",
        "Territory L1",
        "Territory L2",
        "Gender",
        "Age Group",
        "Exception"
      ];

      const visibleCols = columnsOrder.filter(col => visibleColumns[col]);

      const rows = filteredCombinations.map(c => {
        const row = {};
        visibleCols.forEach(col => {
          switch (col) {
            case "Licensor": row[col] = c.licensor || ''; break;
            case "Property": row[col] = c.property; break;
            case "Product L1": row[col] = c.product_l1; break;
            case "Product L2": row[col] = c.product_l2; break;
            case "Product L3": row[col] = c.product_l3 || ''; break;
            case "Channel L1": row[col] = c.channel_l1; break;
            case "Channel L2": row[col] = c.channel_l2; break;
            case "Channel L3": row[col] = c.channel_l3 || ''; break;
            case "Territory":
              row[col] =
                c.territory ||
                c["Territory"] ||
                c.territory_l1 ||
                c["Territory L1"] ||
                c.territory_l2 ||
                c["Territory L2"] ||
                '';
              break;
            case "Territory L1": row[col] = c.territory_l1 || ''; break;
            case "Territory L2": row[col] = c.territory_l2 || ''; break;
            case "Gender": row[col] = c.gender || ''; break;
            case "Age Group": row[col] = c.age_group || ''; break;
            case "Exception": row[col] = c.exceptions || 'N/A'; break;
            default: row[col] = '';
          }
        });
        return row;
      });

      if (!rows.length) {
        alert('No data to export');
        return;
      }

      const header = Object.keys(rows[0]);
      const escapeCell = (v) =>
        `"${String(v ?? '').replace(/\r?\n/g, ' ').replace(/\|/g, '; ').replace(/"/g, '""').trim()}"`;

      const csv =
        '\uFEFF' +
        header.map(h => `"${h}"`).join(',') +
        '\n' +
        rows.map(r => header.map(h => escapeCell(r[h])).join(',')).join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const nameBase = (selectedLicensor?.name || 'combinations').replace(/\s+/g, '_');
      a.href = url;
      a.download = `${nameBase}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }}
  >
   <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-hard-drive-download-icon lucide-hard-drive-download"><path d="M12 2v8"/><path d="m16 6-4 4-4-4"/><rect width="20" height="8" x="2" y="14" rx="2"/><path d="M6 18h.01"/><path d="M10 18h.01"/></svg>
  </button>

  {/* Filter Icon */}
  <button
    type="button"
    className="btn btn-outline-primary btn-sm d-flex align-items-center justify-content-center"
    title="AdvancedFilter"
    onClick={() => {
      if (!showFilterPanel) {
        setTempFilters({ ...combinationFilters });
        setShowColumnsPanel(false);
      }
      setShowFilterPanel(v => !v);
    }}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z" />
    </svg>
  </button>
</div>

        {/* Saved Filter Dropdown */}
        <div className="d-flex align-items-center" style={{ minWidth: '250px' }}>
          <label className="text-nowrap me-2 mb-0 small fw-semibold">Saved Views:</label>
          <select 
            className="form-select form-select-sm"
            value={selectedFilterId}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedFilterId(value);
              
              if (value) {
                const selectedFilter = savedFilters.find(f => f.id === value);
                if (selectedFilter) {
                  handleApplySavedFilter(selectedFilter);
                }
              }
            }}
          >
            <option value="">Select a view...</option>
            {savedFilters.map((filter) => (
              <option key={filter.id} value={filter.id}>
                {filter.name}
              </option>
            ))}
          </select>
        </div>

        <div className="input-group flex-grow-1">
                            <span className="input-group-text bg-white">
                              <Search size={16} />
                            </span>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search..."
                              value={combinationSearch}
                              onChange={(e) => {
                                setCombinationSearch(e.target.value);
                                setCurrentPage(1);
                              }}
                            />

{/* &nbsp;&nbsp;&nbsp;
                            <a
                              title="Refresh"
  href="#"
  onClick={(e) => {
    e.preventDefault();
    setCombinationFilters({ ...tempFilters });
    setCurrentPage(1);
    setShowFilterPanel(false);
    getDataExplorerDetail(prepareFilterParams(tempFilters));
  }}
  style={{ cursor: 'pointer' }}
>
  <RefreshCcw size={18} className="mt-2" />
</a> &nbsp;&nbsp;&nbsp; */}

                            {/* <button className="btn btn-primary">{filteredCombinations.length} combinations</button> */}

                                           {/* Always show pagination - remove the totalPages > 1 condition */}
<div className="d-flex justify-content-end align-items-center mt-1">
  <div className="d-flex align-items-center gap-3">
    <div className="text-muted small">
      &nbsp;&nbsp;
      {filteredCombinations.length > 0 ? (
        <>
          {((currentPage - 1) * rowsPerPage) + 1}–
          {Math.min(currentPage * rowsPerPage, filteredCombinations.length)} of{" "}
          {filteredCombinations.length}
        </>
      ) : (
        "0 of 0"
      )}
    </div>

    <div className="d-flex align-items-center">
      <button
        className="btn btn-sm btn-light border-0"
        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1 || filteredCombinations.length === 0}
        style={{ opacity: (currentPage === 1 || filteredCombinations.length === 0) ? 0.4 : 1 }}
        title="Previous"
      >
        <ChevronLeft size={20} />
      </button>

      <button
        className="btn btn-sm btn-light border-0 ms-2"
        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages || filteredCombinations.length === 0}
        style={{ opacity: (currentPage === totalPages || filteredCombinations.length === 0) ? 0.4 : 1 }}
        title="Next"
      >
        <ChevronRight size={20} />
      </button>
    </div>
  </div>
</div>


                                          </div>


          {/* <button 
                    className="btn btn-sm btn-link p-0" 
                    aria-label="Refresh" 
                    title="Refresh"
                    onClick={() => {
                      // Apply the temporary filters to actual filters
                      setCombinationFilters({...tempFilters});
                      setCurrentPage(1);
                      setShowFilterPanel(false);
                      // Call API with filtered parameters
                      getDataExplorerDetail(prepareFilterParams(tempFilters));
                    }}
                    style={{ lineHeight: 0 }}
                  ></button> */}

                                </div>


                                {/* Filter slide panel (right->left, width 200px) */}
            <div style={panelCommonStyle(showFilterPanel)} aria-hidden={!showFilterPanel}>
              <div className="d-flex justify-content-between align-items-center mb-2 p-3 pb-2">
                <div className="fw-semibold">Filter</div>
                <button className="btn-close" aria-label="Close" onClick={() => {
                  // Reset temp filters to current filters when closing without applying
                  setTempFilters({...combinationFilters});
                  setShowFilterPanel(false);
                }}></button>
              </div>

              <div style={{ flex: 1, overflowY: "auto", padding: "0 16px" }}>
                
                {/* Group 1: Licensor & Property */}
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-2 pb-2 border-bottom">
                    <div className="fw-semibold text-primary" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>
                      LICENSOR & PROPERTY
                    </div>
                  </div>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label small mb-1" style={{ fontWeight: 500 }}>Licensor</label>
                      <ReactSelect
                        options={uniqueCombinationValues.licensor}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option }}
                        onChange={(selected) => {
                          // Clear child filters when parent changes
                          setTempFilters({
                            ...tempFilters, 
                            licensor: Array.isArray(selected) ? selected : [],
                            property: [] // Clear property
                          });
                        }}
                        value={Array.isArray(tempFilters.licensor) ? tempFilters.licensor : []}
                        placeholder="Select Licensor..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '34px',
                            height: '34px',
                            borderRadius: '6px'
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: '34px',
                            padding: '0 8px'
                          }),
                          input: (base) => ({
                            ...base,
                            margin: '0px',
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: '34px',
                          }),
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small mb-1" style={{ fontWeight: 500 }}>Property</label>
                      <ReactSelect
                        options={uniqueCombinationValues.property}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option }}
                        onChange={(selected) => {
                          setTempFilters({...tempFilters, property: Array.isArray(selected) ? selected : []});
                        }}
                        value={Array.isArray(tempFilters.property) ? tempFilters.property : []}
                        placeholder="Select Property..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '34px',
                            height: '34px',
                            borderRadius: '6px'
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: '34px',
                            padding: '0 8px'
                          }),
                          input: (base) => ({
                            ...base,
                            margin: '0px',
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: '34px',
                          }),
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Group 2: Products */}
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-2 pb-2 border-bottom">
                    <div className="fw-semibold text-primary" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>
                      PRODUCTS
                    </div>
                  </div>
                  <div className="row g-2">
                    <div className="col-md-4">
                      <label className="form-label small mb-1" style={{ fontWeight: 500 }}>Product L1</label>
                      <ReactSelect
                        options={uniqueCombinationValues.productL1}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option }}
                        onChange={(selected) => {
                          // Clear child filters when parent changes
                          setTempFilters({
                            ...tempFilters, 
                            productL1: Array.isArray(selected) ? selected : [],
                            productL2: [], // Clear L2
                            productL3: []  // Clear L3
                          });
                        }}
                        value={Array.isArray(tempFilters.productL1) ? tempFilters.productL1 : []}
                        placeholder="Select Product L1..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '34px',
                            height: '34px',
                            borderRadius: '6px'
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: '34px',
                            padding: '0 8px'
                          }),
                          input: (base) => ({
                            ...base,
                            margin: '0px',
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: '34px',
                          }),
                        }}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small mb-1" style={{ fontWeight: 500 }}>Product L2</label>
                      <ReactSelect
                        options={uniqueCombinationValues.productL2}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option }}
                        onChange={(selected) => {
                          // Clear child filter when parent changes
                          setTempFilters({
                            ...tempFilters, 
                            productL2: Array.isArray(selected) ? selected : [],
                            productL3: []  // Clear L3
                          });
                        }}
                        value={Array.isArray(tempFilters.productL2) ? tempFilters.productL2 : []}
                        placeholder="Select Product L2..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '34px',
                            height: '34px',
                            borderRadius: '6px'
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: '34px',
                            padding: '0 8px'
                          }),
                          input: (base) => ({
                            ...base,
                            margin: '0px',
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: '34px',
                          }),
                        }}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small mb-1" style={{ fontWeight: 500 }}>Product L3</label>
                      <ReactSelect
                        options={uniqueCombinationValues.productL3}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option }}
                        onChange={(selected) => {
                          setTempFilters({...tempFilters, productL3: Array.isArray(selected) ? selected : []});
                        }}
                        value={Array.isArray(tempFilters.productL3) ? tempFilters.productL3 : []}
                        placeholder="Select Product L3..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '34px',
                            height: '34px',
                            borderRadius: '6px'
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: '34px',
                            padding: '0 8px'
                          }),
                          input: (base) => ({
                            ...base,
                            margin: '0px',
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: '34px',
                          }),
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Group 3: Channels */}
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-2 pb-2 border-bottom">
                    <div className="fw-semibold text-primary" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>
                      CHANNELS
                    </div>
                  </div>
                  <div className="row g-2">
                    <div className="col-md-4">
                      <label className="form-label small mb-1" style={{ fontWeight: 500 }}>Channel L1</label>
                      <ReactSelect
                        options={uniqueCombinationValues.channelL1}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option }}
                        onChange={(selected) => {
                          // Clear child filters when parent changes
                          setTempFilters({
                            ...tempFilters, 
                            channelL1: Array.isArray(selected) ? selected : [],
                            channelL2: [], // Clear L2
                            channelL3: []  // Clear L3
                          });
                        }}
                        value={Array.isArray(tempFilters.channelL1) ? tempFilters.channelL1 : []}
                        placeholder="Select Channel L1..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '34px',
                            height: '34px',
                            borderRadius: '6px'
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: '34px',
                            padding: '0 8px'
                          }),
                          input: (base) => ({
                            ...base,
                            margin: '0px',
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: '34px',
                          }),
                        }}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small mb-1" style={{ fontWeight: 500 }}>Channel L2</label>
                      <ReactSelect
                        options={uniqueCombinationValues.channelL2}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option }}
                        onChange={(selected) => {
                          // Clear child filter when parent changes
                          setTempFilters({
                            ...tempFilters, 
                            channelL2: Array.isArray(selected) ? selected : [],
                            channelL3: []  // Clear L3
                          });
                        }}
                        value={Array.isArray(tempFilters.channelL2) ? tempFilters.channelL2 : []}
                        placeholder="Select Channel L2..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '34px',
                            height: '34px',
                            borderRadius: '6px'
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: '34px',
                            padding: '0 8px'
                          }),
                          input: (base) => ({
                            ...base,
                            margin: '0px',
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: '34px',
                          }),
                        }}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small mb-1" style={{ fontWeight: 500 }}>Channel L3</label>
                      <ReactSelect
                        options={uniqueCombinationValues.channelL3}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option }}
                        onChange={(selected) => {
                          setTempFilters({...tempFilters, channelL3: Array.isArray(selected) ? selected : []});
                        }}
                        value={Array.isArray(tempFilters.channelL3) ? tempFilters.channelL3 : []}
                        placeholder="Select Channel L3..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '34px',
                            height: '34px',
                            borderRadius: '6px'
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: '34px',
                            padding: '0 8px'
                          }),
                          input: (base) => ({
                            ...base,
                            margin: '0px',
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: '34px',
                          }),
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Group 4: Territory */}
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-2 pb-2 border-bottom">
                    <div className="fw-semibold text-primary" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>
                      TERRITORY
                    </div>
                  </div>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label small mb-1" style={{ fontWeight: 500 }}>Territory L1</label>
                      <ReactSelect
                        options={uniqueCombinationValues.territoryL1}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option }}
                        onChange={(selected) => {
                          // Clear child filter when parent changes
                          setTempFilters({
                            ...tempFilters, 
                            territoryL1: Array.isArray(selected) ? selected : [],
                            territoryL2: []  // Clear L2
                          });
                        }}
                        value={Array.isArray(tempFilters.territoryL1) ? tempFilters.territoryL1 : []}
                        placeholder="Select Territory L1..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '34px',
                            height: '34px',
                            borderRadius: '6px'
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: '34px',
                            padding: '0 8px'
                          }),
                          input: (base) => ({
                            ...base,
                            margin: '0px',
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: '34px',
                          }),
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small mb-1" style={{ fontWeight: 500 }}>Territory L2</label>
                      <ReactSelect
                        options={uniqueCombinationValues.territoryL2}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option }}
                        onChange={(selected) => {
                          setTempFilters({...tempFilters, territoryL2: Array.isArray(selected) ? selected : []});
                        }}
                        value={Array.isArray(tempFilters.territoryL2) ? tempFilters.territoryL2 : []}
                        placeholder="Select Territory L2..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '34px',
                            height: '34px',
                            borderRadius: '6px'
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: '34px',
                            padding: '0 8px'
                          }),
                          input: (base) => ({
                            ...base,
                            margin: '0px',
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: '34px',
                          }),
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Group 5: Store, Gender & Age Group */}
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-2 pb-2 border-bottom">
                    <div className="fw-semibold text-primary" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>
                      DEMOGRAPHICS
                    </div>
                  </div>
                  <div className="row g-2">
                    <div className="col-md-4">
                      <label className="form-label small mb-1" style={{ fontWeight: 500 }}>Store</label>
                      <ReactSelect
                        options={uniqueCombinationValues.store}
                        isClearable
                        onChange={(selected) => {
                          setTempFilters({...tempFilters, store: selected || null});
                        }}
                        value={tempFilters.store}
                        placeholder="Select Store..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '34px',
                            height: '34px',
                            borderRadius: '6px'
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: '34px',
                            padding: '0 8px'
                          }),
                          input: (base) => ({
                            ...base,
                            margin: '0px',
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: '34px',
                          }),
                        }}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small mb-1" style={{ fontWeight: 500 }}>Gender</label>
                      <ReactSelect
                        options={uniqueCombinationValues.gender}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option }}
                        onChange={(selected) => {
                          setTempFilters({...tempFilters, gender: Array.isArray(selected) ? selected : []});
                        }}
                        value={Array.isArray(tempFilters.gender) ? tempFilters.gender : []}
                        placeholder="Select Gender..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '34px',
                            height: '34px',
                            borderRadius: '6px'
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: '34px',
                            padding: '0 8px'
                          }),
                          input: (base) => ({
                            ...base,
                            margin: '0px',
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: '34px',
                          }),
                        }}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small mb-1" style={{ fontWeight: 500 }}>Age Group</label>
                      <ReactSelect
                        options={uniqueCombinationValues.ageGroup}
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option }}
                        onChange={(selected) => {
                          setTempFilters({...tempFilters, ageGroup: Array.isArray(selected) ? selected : []});
                        }}
                        value={Array.isArray(tempFilters.ageGroup) ? tempFilters.ageGroup : []}
                        placeholder="Select Age Group..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '34px',
                            height: '34px',
                            borderRadius: '6px'
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: '34px',
                            padding: '0 8px'
                          }),
                          input: (base) => ({
                            ...base,
                            margin: '0px',
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: '34px',
                          }),
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

      
              <div className="d-flex justify-content-center gap-2 p-3 border-top bg-white" style={{ flexShrink: 0 }}>
        
       
        <button
          className="btn btn-sm btn-primary px-4"
          onClick={() => {
            // Apply the temporary filters to actual filters
            setCombinationFilters({...tempFilters});
            setCurrentPage(1);
            setShowFilterPanel(false);
            
            // Call API with filtered parameters
            getDataExplorerDetail(prepareFilterParams(tempFilters));
          }}
        >
          Apply
        </button>
        <button
          className="btn btn-sm btn-outline-primary px-4"
          onClick={() => {
            const emptyFilters = {
              licensor: [],
              property: [],
              store: null,
              productL1: [],
              productL2: [],
              productL3: [],
              channelL1: [],
              channelL2: [],
              channelL3: [],
              territoryL1: [],
              territoryL2: [],
              gender: [],
              ageGroup: [],
            };
            setTempFilters(emptyFilters);
            setCombinationFilters(emptyFilters);
            setCombinationSearch('');
            setCurrentPage(1);
            setSelectedFilterId(''); // Reset saved filter dropdown
            // Do NOT call API or update table view
          }}
        >
          Clear All
        </button>
              </div>
      
            </div>
      
            {/* Columns slide panel (right->left, width 200px) */}
            <div className='d-none' style={panelCommonStyle(showColumnsPanel)} aria-hidden={!showColumnsPanel}>
                    <div className="d-flex justify-content-between align-items-center mb-2 p-3 pb-2">
                    <div className="fw-semibold" style={{ fontSize: "calc(1rem + 2px)" }}>
  Columns
</div>

                    <button className="btn-close" aria-label="Close" onClick={cancelColumnChanges}></button>
                    </div>
                    <div style={{ flex: 1, overflowY: "auto", padding: "0 12px" }}>
                    <div className="small text-muted mb-2">Toggle visible columns</div>
                    <div className="d-flex align-items-center gap-3 mb-3">
  <a
    onClick={() => {
      const allColumns = {
        "Licensor": true,
        "Property": true,
        "Product L1": true,
        "Product L2": true,
        "Product L3": true,
        "Channel L1": true,
        "Channel L2": true,
        "Channel L3": true,
        "Territory L1": true,
        "Territory L2": true,
        "Gender": true,
        "Age Group": true,
        "Exception": true,
      };
      setTempVisibleColumns(allColumns);
    }}
    className="text-primary cursor-pointer"
  >
    Select All
  </a>

  <a
    onClick={() => {
      const allColumns = {
        "Licensor": false,
        "Property": false,
        "Product L1": false,
        "Product L2": false,
        "Product L3": false,
        "Channel L1": false,
        "Channel L2": false,
        "Channel L3": false,
        "Territory L1": false,
        "Territory L2": false,
        "Gender": false,
        "Age Group": false,
        "Exception": false,
      };
      setTempVisibleColumns(allColumns);
    }}
    className="text-danger cursor-pointer"
  >
    Deselect All
  </a>
</div>

                    <div className="d-flex flex-column gap-2 mb-3">
                    {[
                      "Licensor",
                      "Property",
                      "Product L1",
                      "Product L2",
                      "Product L3",
                      "Channel L1",
                      "Channel L2",
                      "Channel L3",
                      "Territory L1",
                      "Territory L2",
                      "Gender",
                      "Age Group",
                      "Exception",
                    ].map((col, idx) => (
                      <div key={col} className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`col-toggle-${idx}`}
                        checked={tempVisibleColumns[col]}
                        onChange={(e) => handleColumnToggle(col, e.target.checked)}
                      />
                      <label className="form-check-label small" htmlFor={`col-toggle-${idx}`}>
                        {col}
                      </label>
                      </div>
                    ))}
                    </div>
                    </div>

                    <div className="d-flex justify-content-center gap-2 p-3 border-top bg-white" style={{ flexShrink: 0 }}>
                      <button
                        className="btn btn-sm btn-primary px-4"
                        onClick={applyColumnChanges}
                      >
                        Apply
                      </button>
                      <button
                        className="btn btn-sm btn-outline-primary px-4"
                        onClick={cancelColumnChanges}
                      >
                        Cancel
                      </button>
                    </div>
            </div>
                           
                            {/* Applied Filters Display */}
                            {(Object.values(combinationFilters).some(val => Array.isArray(val) ? val.length > 0 : val !== null)) && (
                              <div className="card border-0 shadow-sm mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                                <div className="card-body p-3">
                                  <div className="d-flex align-items-start justify-content-between">
                                    <div className="flex-grow-1">
                                      <div className="d-flex align-items-center mb-2">
                                        <SearchCheck size={18} className="text-primary me-2" />
                                        <strong className="text-primary" style={{ fontSize: '14px' }}>Applied Filters</strong>
                                        <span className="badge bg-primary ms-2">
                                          {Object.values(combinationFilters).reduce((sum, val) => sum + (Array.isArray(val) ? val.length : (val !== null ? 1 : 0)), 0)}
                                        </span>
                                      </div>
                                      <div className="d-flex flex-wrap gap-2">
                                        {combinationFilters.licensor.length > 0 && (
                                          <div className="d-flex flex-wrap gap-1">
                                            {combinationFilters.licensor.map((item, idx) => (
                                              <span key={idx} className="badge bg-light text-dark border d-flex align-items-center" style={{ fontSize: '12px', padding: '6px 10px' }}>
                                                <small className="text-muted me-1">Licensor:</small> {item.label}
                                                <button
                                                  className="btn-close ms-2"
                                                  style={{ fontSize: '8px' }}
                                                  onClick={() => {
                                                    const updated = combinationFilters.licensor.filter((_, i) => i !== idx);
                                                    const updatedFilters = {...combinationFilters, licensor: updated};
                                                    setCombinationFilters(updatedFilters);
                                                    setTempFilters({...tempFilters, licensor: updated});
                                                    getDataExplorerDetail(prepareFilterParams(updatedFilters));
                                                  }}
                                                ></button>
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        {combinationFilters.property.length > 0 && (
                                          <div className="d-flex flex-wrap gap-1">
                                            {combinationFilters.property.map((item, idx) => (
                                              <span key={idx} className="badge bg-light text-dark border d-flex align-items-center" style={{ fontSize: '12px', padding: '6px 10px' }}>
                                                <small className="text-muted me-1">Property:</small> {item.label}
                                                <button
                                                  className="btn-close ms-2"
                                                  style={{ fontSize: '8px' }}
                                                  onClick={() => {
                                                    const updated = combinationFilters.property.filter((_, i) => i !== idx);
                                                    const updatedFilters = {...combinationFilters, property: updated};
                                                    setCombinationFilters(updatedFilters);
                                                    setTempFilters({...tempFilters, property: updated});
                                                    getDataExplorerDetail(prepareFilterParams(updatedFilters));
                                                  }}
                                                ></button>
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        {combinationFilters.store && (
                                          <div className="d-flex flex-wrap gap-1">
                                            <span className="badge bg-light text-dark border d-flex align-items-center" style={{ fontSize: '12px', padding: '6px 10px' }}>
                                              <small className="text-muted me-1">Store:</small> {combinationFilters.store.label}
                                              <button
                                                className="btn-close ms-2"
                                                style={{ fontSize: '8px' }}
                                                onClick={() => {
                                                  const updatedFilters = {...combinationFilters, store: null};
                                                  setCombinationFilters(updatedFilters);
                                                  setTempFilters({...tempFilters, store: null});
                                                  getDataExplorerDetail(prepareFilterParams(updatedFilters));
                                                }}
                                              ></button>
                                            </span>
                                          </div>
                                        )}
                                        {combinationFilters.productL1.length > 0 && (
                                          <div className="d-flex flex-wrap gap-1">
                                            {combinationFilters.productL1.map((item, idx) => (
                                              <span key={idx} className="badge bg-light text-dark border d-flex align-items-center" style={{ fontSize: '12px', padding: '6px 10px' }}>
                                                <small className="text-muted me-1">Product L1:</small> {item.label}
                                                <button
                                                  className="btn-close ms-2"
                                                  style={{ fontSize: '8px' }}
                                                  onClick={() => {
                                                    const updated = combinationFilters.productL1.filter((_, i) => i !== idx);
                                                    const updatedFilters = {...combinationFilters, productL1: updated};
                                                    setCombinationFilters(updatedFilters);
                                                    setTempFilters({...tempFilters, productL1: updated});
                                                    getDataExplorerDetail(prepareFilterParams(updatedFilters));
                                                  }}
                                                ></button>
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        {combinationFilters.productL2.length > 0 && (
                                          <div className="d-flex flex-wrap gap-1">
                                            {combinationFilters.productL2.map((item, idx) => (
                                              <span key={idx} className="badge bg-light text-dark border d-flex align-items-center" style={{ fontSize: '12px', padding: '6px 10px' }}>
                                                <small className="text-muted me-1">Product L2:</small> {item.label}
                                                <button
                                                  className="btn-close ms-2"
                                                  style={{ fontSize: '8px' }}
                                                  onClick={() => {
                                                    const updated = combinationFilters.productL2.filter((_, i) => i !== idx);
                                                    const updatedFilters = {...combinationFilters, productL2: updated};
                                                    setCombinationFilters(updatedFilters);
                                                    setTempFilters({...tempFilters, productL2: updated});
                                                    getDataExplorerDetail(prepareFilterParams(updatedFilters));
                                                  }}
                                                ></button>
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        {combinationFilters.productL3.length > 0 && (
                                          <div className="d-flex flex-wrap gap-1">
                                            {combinationFilters.productL3.map((item, idx) => (
                                              <span key={idx} className="badge bg-light text-dark border d-flex align-items-center" style={{ fontSize: '12px', padding: '6px 10px' }}>
                                                <small className="text-muted me-1">Product L3:</small> {item.label}
                                                <button
                                                  className="btn-close ms-2"
                                                  style={{ fontSize: '8px' }}
                                                  onClick={() => {
                                                    const updated = combinationFilters.productL3.filter((_, i) => i !== idx);
                                                    const updatedFilters = {...combinationFilters, productL3: updated};
                                                    setCombinationFilters(updatedFilters);
                                                    setTempFilters({...tempFilters, productL3: updated});
                                                    getDataExplorerDetail(prepareFilterParams(updatedFilters));
                                                  }}
                                                ></button>
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        {combinationFilters.channelL1.length > 0 && (
                                          <div className="d-flex flex-wrap gap-1">
                                            {combinationFilters.channelL1.map((item, idx) => (
                                              <span key={idx} className="badge bg-light text-dark border d-flex align-items-center" style={{ fontSize: '12px', padding: '6px 10px' }}>
                                                <small className="text-muted me-1">Channel L1:</small> {item.label}
                                                <button
                                                  className="btn-close ms-2"
                                                  style={{ fontSize: '8px' }}
                                                  onClick={() => {
                                                    const updated = combinationFilters.channelL1.filter((_, i) => i !== idx);
                                                    const updatedFilters = {...combinationFilters, channelL1: updated};
                                                    setCombinationFilters(updatedFilters);
                                                    setTempFilters({...tempFilters, channelL1: updated});
                                                    getDataExplorerDetail(prepareFilterParams(updatedFilters));
                                                  }}
                                                ></button>
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        {combinationFilters.channelL2.length > 0 && (
                                          <div className="d-flex flex-wrap gap-1">
                                            {combinationFilters.channelL2.map((item, idx) => (
                                              <span key={idx} className="badge bg-light text-dark border d-flex align-items-center" style={{ fontSize: '12px', padding: '6px 10px' }}>
                                                <small className="text-muted me-1">Channel L2:</small> {item.label}
                                                <button
                                                  className="btn-close ms-2"
                                                  style={{ fontSize: '8px' }}
                                                  onClick={() => {
                                                    const updated = combinationFilters.channelL2.filter((_, i) => i !== idx);
                                                    const updatedFilters = {...combinationFilters, channelL2: updated};
                                                    setCombinationFilters(updatedFilters);
                                                    setTempFilters({...tempFilters, channelL2: updated});
                                                    getDataExplorerDetail(prepareFilterParams(updatedFilters));
                                                  }}
                                                ></button>
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        {combinationFilters.channelL3.length > 0 && (
                                          <div className="d-flex flex-wrap gap-1">
                                            {combinationFilters.channelL3.map((item, idx) => (
                                              <span key={idx} className="badge bg-light text-dark border d-flex align-items-center" style={{ fontSize: '12px', padding: '6px 10px' }}>
                                                <small className="text-muted me-1">Channel L3:</small> {item.label}
                                                <button
                                                  className="btn-close ms-2"
                                                  style={{ fontSize: '8px' }}
                                                  onClick={() => {
                                                    const updated = combinationFilters.channelL3.filter((_, i) => i !== idx);
                                                    const updatedFilters = {...combinationFilters, channelL3: updated};
                                                    setCombinationFilters(updatedFilters);
                                                    setTempFilters({...tempFilters, channelL3: updated});
                                                    getDataExplorerDetail(prepareFilterParams(updatedFilters));
                                                  }}
                                                ></button>
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        {combinationFilters.territoryL1.length > 0 && (
                                          <div className="d-flex flex-wrap gap-1">
                                            {combinationFilters.territoryL1.map((item, idx) => (
                                              <span key={idx} className="badge bg-light text-dark border d-flex align-items-center" style={{ fontSize: '12px', padding: '6px 10px' }}>
                                                <small className="text-muted me-1">Territory L1:</small> {item.label}
                                                <button
                                                  className="btn-close ms-2"
                                                  style={{ fontSize: '8px' }}
                                                  onClick={() => {
                                                    const updated = combinationFilters.territoryL1.filter((_, i) => i !== idx);
                                                    const updatedFilters = {...combinationFilters, territoryL1: updated};
                                                    setCombinationFilters(updatedFilters);
                                                    setTempFilters({...tempFilters, territoryL1: updated});
                                                    getDataExplorerDetail(prepareFilterParams(updatedFilters));
                                                  }}
                                                ></button>
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        {combinationFilters.territoryL2.length > 0 && (
                                          <div className="d-flex flex-wrap gap-1">
                                            {combinationFilters.territoryL2.map((item, idx) => (
                                              <span key={idx} className="badge bg-light text-dark border d-flex align-items-center" style={{ fontSize: '12px', padding: '6px 10px' }}>
                                                <small className="text-muted me-1">Territory L2:</small> {item.label}
                                                <button
                                                  className="btn-close ms-2"
                                                  style={{ fontSize: '8px' }}
                                                  onClick={() => {
                                                    const updated = combinationFilters.territoryL2.filter((_, i) => i !== idx);
                                                    const updatedFilters = {...combinationFilters, territoryL2: updated};
                                                    setCombinationFilters(updatedFilters);
                                                    setTempFilters({...tempFilters, territoryL2: updated});
                                                    getDataExplorerDetail(prepareFilterParams(updatedFilters));
                                                  }}
                                                ></button>
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        {combinationFilters.gender.length > 0 && (
                                          <div className="d-flex flex-wrap gap-1">
                                            {combinationFilters.gender.map((item, idx) => (
                                              <span key={idx} className="badge bg-light text-dark border d-flex align-items-center" style={{ fontSize: '12px', padding: '6px 10px' }}>
                                                <small className="text-muted me-1">Gender:</small> {item.label}
                                                <button
                                                  className="btn-close ms-2"
                                                  style={{ fontSize: '8px' }}
                                                  onClick={() => {
                                                    const updated = combinationFilters.gender.filter((_, i) => i !== idx);
                                                    const updatedFilters = {...combinationFilters, gender: updated};
                                                    setCombinationFilters(updatedFilters);
                                                    setTempFilters({...tempFilters, gender: updated});
                                                    getDataExplorerDetail(prepareFilterParams(updatedFilters));
                                                  }}
                                                ></button>
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        {combinationFilters.ageGroup.length > 0 && (
                                          <div className="d-flex flex-wrap gap-1">
                                            {combinationFilters.ageGroup.map((item, idx) => (
                                              <span key={idx} className="badge bg-light text-dark border d-flex align-items-center" style={{ fontSize: '12px', padding: '6px 10px' }}>
                                                <small className="text-muted me-1">Age Group:</small> {item.label}
                                                <button
                                                  className="btn-close ms-2"
                                                  style={{ fontSize: '8px' }}
                                                  onClick={() => {
                                                    const updated = combinationFilters.ageGroup.filter((_, i) => i !== idx);
                                                    const updatedFilters = {...combinationFilters, ageGroup: updated};
                                                    setCombinationFilters(updatedFilters);
                                                    setTempFilters({...tempFilters, ageGroup: updated});
                                                    getDataExplorerDetail(prepareFilterParams(updatedFilters));
                                                  }}
                                                ></button>
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                     <button
          className="btn btn-sm btn-primary px-4"
          onClick={() => {
            setShowFilterPanel(false);
            setTempVisibleColumns({...visibleColumns}); // Sync temp columns with current visible columns
            setShowSaveFilterModal(true);
            setActiveFilterTab('create');
          }}
        >
          Save View
        </button>

                                    <button
                                      className="btn btn-sm btn-outline-danger ms-3 flex-shrink-0"
                                      onClick={() => {
                                                                               const emptyFilters = {
                                          licensor: [],
                                          property: [], // Add this line
                                          productL1: [],
                                          productL2: [],
                                          productL3: [],
                                          channelL1: [],
                                          channelL2: [],
                                          channelL3: [],
                                          territoryL1: [],
                                          territoryL2: [],
                                          gender: [],
                                          ageGroup: [],
                                        };
                                        setCombinationFilters(emptyFilters);
                                        setTempFilters(emptyFilters);
                                        setCurrentPage(1);
                                        setSelectedFilterId(''); // Reset saved filter dropdown
                                        
                                        // Call API with empty filters
                                        getDataExplorerDetail({});
                                      }}
                                      title="Clear all filters"
                                    >
                                      Clear All
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* <div className="card shadow-sm">
                              <div className="card-body"> */}
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <div>
                                <div className="small text-muted d-none">
                                  Licensor: <span className="fw-semibold">{selectedLicensor?.name}</span> |
                                  Properties: <span className="fw-semibold">{selectedProperties.join(', ')}</span> |
                                  Total Combinations: <span className="fw-semibold">{getRelevantCombinations().length}</span> |
                                  Filtered: <span className="fw-semibold">{filteredCombinations.length}</span>
                                </div>
                                </div>
                                <div style={{ display: "none", gap: "8px", marginTop: "25px" }}>

                                  

  <button
    className="btn btn-outline-primary btn-sm"
    onClick={() => {
      setCombinationFilters({
        productL1: [],
        productL2: [],
        productL3: [],
        channelL1: [],
        channelL2: [],
        channelL3: [],
        territoryL1: [],
        territoryL2: [],
        gender: [],
        ageGroup: [],
      });
      setCombinationSearch('');
      setCurrentPage(1);
      setSelectedFilterId(''); // Reset saved filter dropdown
    }}
  >
    Clear All
  </button>

  <button
    className="btn btn-outline-primary btn-sm"
    onClick={() => {
      // Filter table data using selected filters and data array from API
      const filteredCombinations = (() => {
        if (!tableData?.data) return [];
        const filtered = tableData.data.filter(c => {
          const matchesFilters =
            (combinationFilters.licensor.length === 0 || combinationFilters.licensor.some(f => f.value === c.licensor)) &&
            (combinationFilters.property.length === 0 || combinationFilters.property.some(f => f.value === c.property)) &&
            (combinationFilters.productL1.length === 0 || combinationFilters.productL1.some(f => f.value === c["Product Category (L1)"])) &&
            (combinationFilters.productL2.length === 0 || (combinationFilters.productL1.length === 0 ? true : combinationFilters.productL2.some(f => f.value === c["Product SubCategory (L2)"]) && combinationFilters.productL1.some(f => f.value === c["Product Category (L1)"]))) &&
            (combinationFilters.productL3.length === 0 || (combinationFilters.productL2.length === 0 ? true : combinationFilters.productL3.some(f => f.value === c["Product (L3)"]) && combinationFilters.productL2.some(f => f.value === c["Product SubCategory (L2)"]))) &&
            (combinationFilters.channelL1.length === 0 || combinationFilters.channelL1.some(f => f.value === c["Channel (L1)"])) &&
            (combinationFilters.channelL2.length === 0 || (combinationFilters.channelL1.length === 0 ? true : combinationFilters.channelL2.some(f => f.value === c["Channel SubType (L2)"]) && combinationFilters.channelL1.some(f => f.value === c["Channel (L1)"]))) &&
            (combinationFilters.channelL3.length === 0 || (combinationFilters.channelL2.length === 0 ? true : combinationFilters.channelL3.some(f => f.value === c["Channel (L3)"]) && combinationFilters.channelL2.some(f => f.value === c["Channel SubType (L2)"]))) &&
            (combinationFilters.territoryL1.length === 0 || combinationFilters.territoryL1.some(f => f.value === c["Territory (L1)"])) &&
            (combinationFilters.territoryL2.length === 0 || (combinationFilters.territoryL1.length === 0 ? true : combinationFilters.territoryL2.some(f => f.value === c["Territory Zone (L2)"]) && combinationFilters.territoryL1.some(f => f.value === c["Territory (L1)"]))) &&
            (combinationFilters.gender.length === 0 || combinationFilters.gender.some(f => f.value === c.gender)) &&
            (combinationFilters.ageGroup.length === 0 || combinationFilters.ageGroup.some(f => f.value === c.age_group));

          const matchesSearch = combinationSearch === '' || 
            Object.values(c).some(val => 
              String(val).toLowerCase().includes(combinationSearch.toLowerCase())
            );

          return matchesFilters && matchesSearch;
        });

        // Then apply sorting
        if (sortConfig.key) {
          filtered.sort((a, b) => {
            const aValue = a[sortConfig.key] || '';
            const bValue = b[sortConfig.key] || '';
            // Handle string comparison
            const comparison = String(aValue).localeCompare(String(bValue), undefined, { numeric: true });
            return sortConfig.direction === 'asc' ? comparison : -comparison;
          });
        }

        return filtered;
      })();
      URL.revokeObjectURL(url);
    }}
  >
    Export XLS
  </button>
</div>


                      
                              </div>

                    {/* Show loading overlay while fetching combinations */}
                    {loading && (
                      <div className="position-relative">
                        <div className="position-absolute top-50 start-50 translate-middle" style={{ zIndex: 1000 }}>
                          <div className="text-center py-4 bg-white rounded shadow-sm p-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading combinations...</span>
                            </div>
                            <div className="mt-2 text-muted">Loading data, please wait...</div>
                          </div>
                        </div>
                        <div style={{ opacity: 0.3, pointerEvents: 'none' }}>
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th className="small">Loading...</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td className="text-center text-muted py-4">Loading data...</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {!loading && (
                      <>
                        {/* Wrap table in container with fixed height */}
                        <div style={tableContainerStyle} className="table-container">
                          <div style={{ minWidth: '1800px' }}> {/* Wrapper to force horizontal scroll */}
    <table className="table table-sm table-hover mb-0" style={{ 
      tableLayout: 'fixed',
      width: '100%',
      minWidth: '1800px' // Force table to be wide
    }}>
                            <thead className="table-light">
                              <tr>
                                {visibleColumns["Licensor"] && (
            <th style={{...stickyHeaderStyle, width: '120px'}}
            onClick={() => handleSort('licensor')}
            title="Click to sort"
          >
            <div className="d-flex align-items-center justify-content-between">
              Licensor
              <SortIcon columnKey="licensor" />
            </div>
          </th>
          )}
          {visibleColumns["Property"] && (
            <th style={{...stickyHeaderStyle, width: '150px'}}
            onClick={() => handleSort('property')}
            title="Click to sort"
          >
            <div className="d-flex align-items-center justify-content-between">
              Property
              <SortIcon columnKey="property" />
            </div>
          </th>
          )}
          {visibleColumns["Exception"] && (
            <th className="text-center" style={{...stickyHeaderStyle, width: '90px', cursor: 'default'}}
            title="Not sortable"
          >
            Exception
          </th>
          )}
          {visibleColumns["Product L1"] && (
            <th style={{...stickyHeaderStyle, width: '140px'}}
            onClick={() => handleSort('product_l1')}
            title="Click to sort"
          >
            <div className="d-flex align-items-center justify-content-between">
              Product L1
              <SortIcon columnKey="product_l1" />
            </div>
          </th>
          )}
          {visibleColumns["Product L2"] && (
            <th style={{...stickyHeaderStyle, width: '140px'}}
            onClick={() => handleSort('product_l2')}
            title="Click to sort"
          >
            <div className="d-flex align-items-center justify-content-between">
              Product L2
              <SortIcon columnKey="product_l2" />
            </div>
          </th>
          )}
          {visibleColumns["Product L3"] && (
            <th style={{...stickyHeaderStyle, width: '140px'}}
            onClick={() => handleSort('product_l3')}
            title="Click to sort"
          >
            <div className="d-flex align-items-center justify-content-between">
              Product L3
              <SortIcon columnKey="product_l3" />
            </div>
          </th>
          )}
          {visibleColumns["Channel L1"] && (
            <th style={{...stickyHeaderStyle, width: '130px'}}
            onClick={() => handleSort('channel_l1')}
            title="Click to sort"
          >
            <div className="d-flex align-items-center justify-content-between">
              Channel L1
              <SortIcon columnKey="channel_l1" />
            </div>
          </th>
          )}
          {visibleColumns["Channel L2"] && (
            <th style={{...stickyHeaderStyle, width: '130px'}}
            onClick={() => handleSort('channel_l2')}
            title="Click to sort"
          >
            <div className="d-flex align-items-center justify-content-between">
              Channel L2
              <SortIcon columnKey="channel_l2" />
            </div>
          </th>
          )}
          {visibleColumns["Channel L3"] && (
            <th style={{...stickyHeaderStyle, width: '130px'}}
            onClick={() => handleSort('channel_l3')}
            title="Click to sort"
          >
            <div className="d-flex align-items-center justify-content-between">
              Channel L3
              <SortIcon columnKey="channel_l3" />
            </div>
          </th>
          )}
          {visibleColumns["Territory"] && (
            <th style={{...stickyHeaderStyle, width: '180px'}}
                onClick={() => handleSort('territory')}
                title="Click to sort">
              <div className="d-flex align-items-center justify-content-between">
                Territory
                <SortIcon columnKey="territory" />
              </div>
            </th>
          )}
          {visibleColumns["Gender"] && (
            <th style={{...stickyHeaderStyle, width: '100px'}}
            onClick={() => handleSort('gender')}
            title="Click to sort"
          >
            <div className="d-flex align-items-center justify-content-between">
              Gender
              <SortIcon columnKey="gender" />
            </div>
          </th>
          )}
          {visibleColumns["Age Group"] && (
            <th style={{...stickyHeaderStyle, width: '110px'}}
            onClick={() => handleSort('age_group')}
            title="Click to sort"
          >
            <div className="d-flex align-items-center justify-content-between">
              Age Group
              <SortIcon columnKey="age_group" />
            </div>
          </th>
          )}
      </tr>
    </thead>
    <tbody>
      {paginatedCombinations.length > 0 ? (
        paginatedCombinations.map((combo) => (
          <tr key={combo.id}>
            {visibleColumns["Licensor"] && (
                <td style={{...cellStyle, width: '120px'}}>
                  {combo.licensor}
                </td>
              )}
              {visibleColumns["Property"] && (
                <td className="fw-semibold text-primary" style={{...cellStyle, width: '150px'}}>
                  {combo.property}
                </td>
              )}
              {visibleColumns["Exception"] && (
                <td className="text-center" style={{...cellStyle, width: '90px'}}>
                  {(combo.exceptions || combo.exceptions) ? (
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      onClick={() => openExceptionModal(combo.exceptions || combo.exceptions)}
                      title="View exception"
                    >
                      <AlertTriangle size={14} className="text-warning" />
                    </button>
                  ) : (
                    <span className="text-muted" style={{fontSize: '10px'}}>N/A</span>
                  )}
                </td>
              )}
              {visibleColumns["Product L1"] && (
                <td style={{...cellStyle, width: '140px'}}>
                  {combo.product_l1}
                </td>
              )}
              {visibleColumns["Product L2"] && (
                <td style={{...cellStyle, width: '140px'}}>
                  {combo.product_l2}
                </td>
              )}
              {visibleColumns["Product L3"] && (
                <td style={{...cellStyle, width: '140px'}}>
                  {combo.product_l3}
                </td>
              )}
              {visibleColumns["Channel L1"] && (
                <td style={{...cellStyle, width: '130px'}}>
                  {combo.channel_l1}
                </td>
              )}
              {visibleColumns["Channel L2"] && (
                <td style={{...cellStyle, width: '130px'}}>
                  {combo.channel_l2}
                </td>
              )}
              {visibleColumns["Channel L3"] && (
                <td style={{...cellStyle, width: '130px'}}>
                  {combo.channel_l3 || 'N/A'}
                </td>
              )}
              {visibleColumns["Territory"] && (
                <td style={{...cellStyle, width: '180px', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer'}}
                  title={combo.territory}
                >
                  {combo.territory}
                </td>
              )}
              {visibleColumns["Gender"] && (
                <td style={{...cellStyle, width: '100px'}}>
                  {combo.gender || 'N/A'}
                </td>
              )}
              {visibleColumns["Age Group"] && (
                <td style={{...cellStyle, width: '110px'}}>
                  {combo.age_group || 'N/A'}
                </td>
              )}
          </tr>
          
        ))
      ) : (
        !loading && (
          <tr>
            <td 
              colSpan={Object.values(visibleColumns).filter(Boolean).length} 
              className="text-center text-muted"
              style={{padding: '20px', fontSize: '12px'}}
            >
              {tableData && tableData.data && tableData.data.length > 0 
                ? 'No match your filters' 
                : 'No data available'}
            </td>
          </tr>
        )
      )}
    </tbody>
  </table>
  </div>
</div>

{/* Keep the same CSS */}
<style jsx>{`
  .table-container {
    scrollbar-width: thin;
    scrollbar-color: #888 #f1f1f1;
    overflow-x: scroll !important;
    overflow-y: scroll !important;
  }

  .table-container::-webkit-scrollbar {
    width: 12px;
    height: 12px;
  }

  .table-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  .table-container::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  .table-container::-webkit-scrollbar-thumb:hover {
    background: #555;
  }

  .table-container::-webkit-scrollbar-corner {
    background: #f1f1f1;
  }

  .table-container table {
    margin-bottom: 10px !important;
  }

  .table-container tbody tr:hover {
    background-color: #f8f9fa;
  }

  .table-container tbody tr:last-child td {
    border-bottom: 1px solid #dee2e6;
  }
`}</style>
                      </>
                    )}
                  {/* </div>
                          </div> */}
             
            </div>
          )} </>)}
                        {/* Exception details modal */}
                        {showExceptionModal && (
                          <>
  <div className="modal-backdrop fade show"></div>
  <div
    className="modal d-block"
    tabIndex="-1"
    role="dialog"
    aria-modal="true"
    style={{ backdropFilter: "blur(4px)" }}
  >
    <div className="modal-dialog modal-dialog-centered modal-lg">
      <div className="modal-content shadow-lg border-0 rounded-3">
        <div
          className="modal-header border-0"
          style={{ backgroundColor: "#f8f9fa", borderTopLeftRadius: "12px", borderTopRightRadius: "12px" }}
        >
          <h5 className="modal-title fw-semibold text-primary mb-0">
            <i className="bi bi-exclamation-circle me-2 text-danger"></i>
            Exception Details
          </h5>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={closeExceptionModal}
          ></button>
        </div>

        <div
          className="modal-body px-4 py-3"
          style={{
            maxHeight: "500px",
            overflowY: "auto",
            backgroundColor: "#ffffff",
          }}
        >
          {exceptionContent ? (
           <ol
  className="list-group"
  style={{
    borderRadius: "8px",
    counterReset: "item",
    paddingLeft: "0",
  }}
>
  {exceptionContent.split("|").map((item, index) => (
    <li
      key={index}
      className="list-group-item border-0 mb-2 shadow-sm d-flex align-items-center"
      style={{
        borderRadius: "6px",
        backgroundColor: "#f8f9fa",
        transition: "transform 0.2s ease, background-color 0.2s ease",
        cursor: "default",
        padding: "6px 10px",
        fontSize: "13px",
        lineHeight: "1.3",
        fontSize: "10px",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.backgroundColor = "#e9ecef")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.backgroundColor = "#f8f9fa")
      }
    >
      <span
        style={{
          fontWeight: 600,
          color: "#0d6efd",
          marginRight: "8px",
          minWidth: "20px",
          textAlign: "right",
          fontSize: "12px",
        }}
      >
        {index + 1}.
      </span>
      <span className="text-dark flex-grow-1">{item.trim()}</span>
    </li>
  ))}
</ol>


          ) : (
            <div className="text-center text-muted py-4">
              <i className="bi bi-info-circle me-2"></i>
              No exception data available
            </div>
          )}
        </div>

        <div
          className="modal-footer border-0"
          style={{ backgroundColor: "#f8f9fa", borderBottomLeftRadius: "12px", borderBottomRightRadius: "12px" }}
        >
          <button
            className="btn btn-sm btn-primary px-4"
            onClick={closeExceptionModal}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
                          </>
                        )}

      {/* Save Filter Modal */}
      {showSaveFilterModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content shadow-lg border-0">
            <div className="modal-header bg-light border-bottom">
              <div className="d-flex align-items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2 text-primary">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <h5 className="modal-title mb-0 fw-bold">Filter Manager</h5>
              </div>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => {
                  setShowSaveFilterModal(false);
                  setFilterName('');
                  setActiveFilterTab('create');
                }}
              ></button>
            </div>
            
            {/* Tabs */}
            
            <div className="modal-body" style={{ backgroundColor: '#f9fafb' }}>
              <>
              <div className="mb-4">
                <label className="form-label fw-semibold">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                    <path d="M12 20h9"/>
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                  </svg>
                  Filter Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control form-control-lg shadow-sm"
                  placeholder="e.g., North America Wholesale Apparel"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  autoFocus
                  style={{ borderColor: '#e5e7eb' }}
                />
              </div>


              <div className="mb-3">
                <label className="form-label fw-semibold text-secondary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                    <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                    <polyline points="2 17 12 22 22 17"/>
                    <polyline points="2 12 12 17 22 12"/>
                  </svg>
                  Applied Filters Preview:
                </label>
                <div className="border rounded-3 p-3 shadow-sm" style={{ backgroundColor: 'white', maxHeight: '300px', overflowY: 'auto' }}>
                  {combinationFilters.licensor.length > 0 && (
                    <div className="mb-2">
                      <strong className="small">Licensor:</strong>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {combinationFilters.licensor.map((item, idx) => (
                          <span key={idx} className="badge bg-primary">{item.label}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {combinationFilters.property.length > 0 && (
                    <div className="mb-2">
                      <strong className="small">Property:</strong>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {combinationFilters.property.map((item, idx) => (
                          <span key={idx} className="badge bg-primary">{item.label}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {combinationFilters.store && (
                    <div className="mb-2">
                      <strong className="small">Store:</strong>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        <span className="badge bg-primary">{combinationFilters.store.label}</span>
                      </div>
                    </div>
                  )}
                  {combinationFilters.productL1.length > 0 && (
                    <div className="mb-2">
                      <strong className="small">Product L1:</strong>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {combinationFilters.productL1.map((item, idx) => (
                          <span key={idx} className="badge bg-light">{item.label}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {combinationFilters.productL2.length > 0 && (
                    <div className="mb-2">
                      <strong className="small">Product L2:</strong>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {combinationFilters.productL2.map((item, idx) => (
                          <span key={idx} className="badge bg-light">{item.label}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {combinationFilters.productL3.length > 0 && (
                    <div className="mb-2">
                      <strong className="small">Product L3:</strong>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {combinationFilters.productL3.map((item, idx) => (
                          <span key={idx} className="badge bg-light">{item.label}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {combinationFilters.channelL1.length > 0 && (
                    <div className="mb-2">
                      <strong className="small">Channel L1:</strong>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {combinationFilters.channelL1.map((item, idx) => (
                          <span key={idx} className="badge bg-info">{item.label}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {combinationFilters.channelL2.length > 0 && (
                    <div className="mb-2">
                      <strong className="small">Channel L2:</strong>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {combinationFilters.channelL2.map((item, idx) => (
                          <span key={idx} className="badge bg-info">{item.label}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {combinationFilters.channelL3.length > 0 && (
                    <div className="mb-2">
                      <strong className="small">Channel L3:</strong>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {combinationFilters.channelL3.map((item, idx) => (
                          <span key={idx} className="badge bg-info">{item.label}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {combinationFilters.territoryL1.length > 0 && (
                    <div className="mb-2">
                      <strong className="small">Territory L1:</strong>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {combinationFilters.territoryL1.map((item, idx) => (
                          <span key={idx} className="badge bg-warning text-dark">{item.label}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {combinationFilters.territoryL2.length > 0 && (
                    <div className="mb-2">
                      <strong className="small">Territory L2:</strong>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {combinationFilters.territoryL2.map((item, idx) => (
                          <span key={idx} className="badge bg-warning text-dark">{item.label}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {combinationFilters.gender.length > 0 && (
                    <div className="mb-2">
                      <strong className="small">Gender:</strong>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {combinationFilters.gender.map((item, idx) => (
                          <span key={idx} className="badge bg-secondary">{item.label}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {combinationFilters.ageGroup.length > 0 && (
                    <div className="mb-2">
                      <strong className="small">Age Group:</strong>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {combinationFilters.ageGroup.map((item, idx) => (
                          <span key={idx} className="badge bg-secondary">{item.label}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Column Visibility Section */}
              <div className="card border shadow-sm mb-3">
                <div className="card-header bg-white border-bottom">
                  <div className="d-flex align-items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2 text-primary">
                      <rect x="3" y="3" width="7" height="7"/>
                      <rect x="14" y="3" width="7" height="7"/>
                      <rect x="14" y="14" width="7" height="7"/>
                      <rect x="3" y="14" width="7" height="7"/>
                    </svg>
                    <h6 className="mb-0 fw-bold">Column Visibility</h6>
                    <span className="ms-auto badge bg-primary rounded-pill">
                      {Object.values(tempVisibleColumns).filter(v => v).length}/{Object.keys(tempVisibleColumns).length} visible
                    </span>
                  </div>
                </div>
                <div className="card-body">
                  <div className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center" style={{ fontSize: '12px' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2 flex-shrink-0">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="16" x2="12" y2="12"/>
                      <line x1="12" y1="8" x2="12.01" y2="8"/>
                    </svg>
                    <span>Configure column visibility for this filter. Columns will only change when you load a saved filter with specific column settings.</span>
                  </div>

                    {/* Quick Actions */}
                  <div className="d-flex gap-2 mt-3 pt-3 border-top mb-3">
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        const allVisible = {};
                        Object.keys(tempVisibleColumns).forEach(key => {
                          allVisible[key] = true;
                        });
                        setTempVisibleColumns(allVisible);
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      Select All
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        const allHidden = {};
                        Object.keys(tempVisibleColumns).forEach(key => {
                          allHidden[key] = false;
                        });
                        setTempVisibleColumns(allHidden);
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                      Deselect All
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-primary ms-auto"
                      onClick={() => {
                        const defaultColumns = {
                          "Licensor": true,
                          "Property": true,
                          "Product L1": true,
                          "Product L2": true,
                          "Product L3": true,
                          "Channel L1": true,
                          "Channel L2": true,
                          "Channel L3": true,
                          "Territory L1": true,
                          "Territory L2": true,
                          "Gender": true,
                          "Age Group": true,
                          "Exception": true,
                        };
                        setTempVisibleColumns(defaultColumns);
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                        <polyline points="1 4 1 10 7 10"/>
                        <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                      </svg>
                      Reset to Default
                    </button>
                  </div>
                  <div className="row g-2">
                    {Object.entries(tempVisibleColumns).map(([colName, isVisible]) => (
                      <div key={colName} className="col-6 col-md-4 col-lg-3">
                        <div 
                          className={`d-flex align-items-center gap-2 p-2 rounded border ${isVisible ? 'bg-light bg-opacity-10 border-light' : 'bg-light bg-opacity-10 border-light'}`}
                          style={{ 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontSize: '9px'
                          }}
                          onClick={() => setTempVisibleColumns(prev => ({
                            ...prev,
                            [colName]: !prev[colName]
                          }))}
                        >
                          <div style={{ minWidth: '16px', display: 'flex', alignItems: 'center' }}>
                            {isVisible ? (
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#198754" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>
                            ) : (
                              <div style={{ 
                                width: '14px', 
                                height: '14px', 
                                border: '2px solid #ddd', 
                                borderRadius: '3px',
                                backgroundColor: 'white'
                              }}></div>
                            )}
                          </div>
                          <span 
                           style={{ 
                            fontSize: '12px'
                          }}

                          className={`flex-grow-1 ${isVisible ? 'text-muted' : 'text-muted'}`}>
                            {colName}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                
                </div>
              </div>
              </>
            </div>
           
              <div className="modal-footer bg-light border-top">
                <button 
                  type="button" 
                  className="btn btn-outline-primary"
                  onClick={() => {
                    setShowSaveFilterModal(false);
                    setFilterName('');
                    setActiveFilterTab('create');
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary shadow-sm"
                  onClick={handleSaveFilter}
                  disabled={!filterName.trim() || savingFilter}
                >
                  {savingFilter ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                        <polyline points="17 21 17 13 7 13 7 21"/>
                        <polyline points="7 3 7 8 15 8"/>
                      </svg>
                      Save Filter
                    </>
                  )}
                </button>
              </div>
           
          </div>
        </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}